import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { handleUppercaseInput } from "@/lib/utils";
import { API, BACKEND_URL } from "@/lib/api";
import { useGarageBrandingFull } from "@/hooks/useGarageBranding";
import { useAuth } from "@/context/AuthContext";
import { statusConfig, issuePriorityConfig, issueStatusConfig, issueCategoryConfig } from "@/lib/uiConfig";
import DTCManagementSection from "@/components/DTCManagementSection";
import WorkHoldManager from "@/components/WorkHoldManager";
import TimeTracker from "@/components/TimeTracker";
import JobCardStatusWorkflow from "@/components/JobCardStatusWorkflow";
import AddVehicleDataForm from "@/components/forms/AddVehicleDataForm";
import SimpleAddPartForm from "@/components/forms/SimpleAddPartForm";
import JobCardUsedItems from "@/components/JobCardUsedItems";
import IssueMediaAttachments from "@/components/IssueMediaAttachments";
import VehicleCascadeDropdowns from "@/components/VehicleCascadeDropdowns";
import { useFormDraft, DraftRestoredBar } from "@/components/FormDraft";
import {
  Plus, Car, Wrench, AlertTriangle, CheckCircle, Clock, Package,
  Search, Bell, Settings, Users, Calendar, FileText, BarChart3,
  Home, Archive, DollarSign, Phone, Mail, MapPin, Menu, X,
  AlertCircle, Bug, Zap, Settings2, Gauge, Cog, Calculator,
  Eye, Download, Camera, Edit, Trash, RefreshCw, LogOut, LogIn,
  UserPlus, Shield, PackageX, Trash2, Play, List, Clipboard, Database, Minus,
  Check, CreditCard, Truck, Send, Code, FileCheck, Activity,
  TrendingUp, TrendingDown, ArrowUpCircle, ArrowDownCircle, Loader2,
  Filter, Info, ClipboardList, ChevronDown, RotateCcw, History
} from "lucide-react";

const JOBCARD_ACTIVITY_STYLES = {
  status_change: 'bg-blue-100 text-blue-800',
  technician_assigned: 'bg-purple-100 text-purple-800',
  item_added: 'bg-green-100 text-green-800',
  item_removed: 'bg-red-100 text-red-800',
  hold: 'bg-amber-100 text-amber-800',
  resume: 'bg-emerald-100 text-emerald-800',
  work_started: 'bg-teal-100 text-teal-800',
  work_stopped: 'bg-gray-100 text-gray-700',
  rework: 'bg-orange-100 text-orange-800',
};
const JOBCARD_ACTIVITY_LABELS = {
  status_change: 'Status Change',
  technician_assigned: 'Technician',
  item_added: 'Item Added',
  item_removed: 'Item Removed',
  hold: 'On Hold',
  resume: 'Resumed',
  work_started: 'Work Started',
  work_stopped: 'Work Stopped',
  rework: 'Rework',
};

// Job Card Activity Log — chronological record of status changes, technician
// assignment, items added/removed and hold/resume/work events, newest first.
const JobCardActivityLogSection = ({ activityLog }) => {
  const entries = [...(activityLog || [])].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <Card data-testid="jobcard-activity-log-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
          <History className="h-4 w-4" /> Activity Log
        </CardTitle>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="jobcard-activity-log-empty">
            No activity recorded yet for this job card.
          </p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto pr-1" data-testid="jobcard-activity-log-list">
            {entries.map((entry) => (
              <div key={entry.id} className="border-l-2 border-gray-200 pl-3 py-1" data-testid={`jobcard-activity-entry-${entry.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={JOBCARD_ACTIVITY_STYLES[entry.action] || 'bg-gray-100 text-gray-700'}>
                    {JOBCARD_ACTIVITY_LABELS[entry.action] || entry.action}
                  </Badge>
                  <span className="text-xs text-gray-500">{new Date(entry.timestamp).toLocaleString('en-IN')}</span>
                  {entry.actor && <span className="text-xs text-gray-400">by {entry.actor}</span>}
                </div>
                <p className="text-sm text-gray-800 mt-0.5">{entry.description}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Capitalizes the first letter of each word — used for Category/Sub-Category
// display labels (stored lowercase in the DB, e.g. "engine", "fuel system").
const capitalizeWords = (str) => {
  if (!str) return str;
  return str.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const TIME_LOG_ACTIVITY_TYPES = {
  general: { label: 'General Work', color: 'bg-blue-100 text-blue-800' },
  diagnosis: { label: 'Diagnosis', color: 'bg-purple-100 text-purple-800' },
  repair: { label: 'Repair Work', color: 'bg-green-100 text-green-800' },
  testing: { label: 'Testing', color: 'bg-yellow-100 text-yellow-800' },
  parts_installation: { label: 'Parts Installation', color: 'bg-orange-100 text-orange-800' }
};

const TIME_LOG_STATUS_STYLES = {
  active: 'bg-emerald-100 text-emerald-800',
  paused: 'bg-amber-100 text-amber-800',
  completed: 'bg-gray-100 text-gray-700'
};

const formatMinutes = (minutes) => {
  const m = Math.round(minutes || 0);
  if (m < 60) return `${m}m`;
  const hrs = Math.floor(m / 60);
  const mins = m % 60;
  return `${hrs}h ${mins}m`;
};

// Time Tracking Log — fetches /jobcards/{id}/time-summary and displays the
// full technician/activity breakdown plus a chronological session log.
const TimeTrackingLogSection = ({ jobCardId }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchSummary = async () => {
      try {
        const response = await axios.get(`${API}/jobcards/${jobCardId}/time-summary`);
        if (isMounted) setSummary(response.data);
      } catch (error) {
        console.error("Error fetching time tracking summary:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchSummary();
    return () => { isMounted = false; };
  }, [jobCardId]);

  if (loading) {
    return (
      <Card data-testid="time-tracking-log-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
            <Clock className="h-4 w-4" /> Time Tracking Log
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading time tracking log...
          </div>
        </CardContent>
      </Card>
    );
  }

  const dailyLogs = summary?.daily_logs || [];
  const allSessions = dailyLogs
    .flatMap((log) => (log.sessions || []).map((s) => ({ ...s, log_date: log.log_date })))
    .sort((a, b) => new Date(b.started_at) - new Date(a.started_at));

  if (!summary || (dailyLogs.length === 0 && (summary.active_sessions || []).length === 0)) {
    return (
      <Card data-testid="time-tracking-log-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
            <Clock className="h-4 w-4" /> Time Tracking Log
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500" data-testid="time-tracking-empty-state">
            No time tracking activity recorded yet for this job card.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="time-tracking-log-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
          <Clock className="h-4 w-4" /> Time Tracking Log
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Summary stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="border rounded p-3 bg-gray-50" data-testid="time-log-total-worked">
            <p className="text-xs text-gray-500">Total Worked</p>
            <p className="text-lg font-bold text-gray-900">{formatMinutes(summary.total_worked_minutes)}</p>
          </div>
          <div className="border rounded p-3 bg-gray-50" data-testid="time-log-billable">
            <p className="text-xs text-gray-500">Billable Time</p>
            <p className="text-lg font-bold text-green-700">{formatMinutes(summary.billable_minutes)}</p>
          </div>
          <div className="border rounded p-3 bg-gray-50" data-testid="time-log-hold-duration">
            <p className="text-xs text-gray-500">On Hold Duration</p>
            <p className="text-lg font-bold text-amber-700">{formatMinutes(summary.total_hold_duration_minutes)}</p>
          </div>
          <div className="border rounded p-3 bg-gray-50" data-testid="time-log-active-sessions">
            <p className="text-xs text-gray-500">Active Now</p>
            <p className="text-lg font-bold text-emerald-700">
              {(summary.active_sessions || []).length > 0 ? `${summary.active_sessions.length} technician(s)` : 'None'}
            </p>
          </div>
        </div>

        {/* Technician Breakdown */}
        {Object.keys(summary.technician_breakdown || {}).length > 0 && (
          <div>
            <p className="text-xs text-gray-500 font-medium mb-2">Technician Breakdown</p>
            <div className="space-y-2">
              {Object.entries(summary.technician_breakdown).map(([tech, data]) => (
                <div key={tech} className="flex items-center justify-between border rounded px-3 py-2 text-sm" data-testid={`time-log-tech-${tech}`}>
                  <span className="font-medium">{tech}</span>
                  <span className="text-gray-600">
                    {formatMinutes(data.total_time_minutes)} total · {formatMinutes(data.billable_time_minutes)} billable · {data.sessions_count} session{data.sessions_count !== 1 ? 's' : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Activity Breakdown */}
        {Object.keys(summary.activity_breakdown || {}).length > 0 && (
          <div>
            <p className="text-xs text-gray-500 font-medium mb-2">Activity Breakdown</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(summary.activity_breakdown).map(([activity, minutes]) => (
                <Badge key={activity} className={TIME_LOG_ACTIVITY_TYPES[activity]?.color || 'bg-gray-100 text-gray-800'}>
                  {TIME_LOG_ACTIVITY_TYPES[activity]?.label || activity}: {formatMinutes(minutes)}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Active Sessions */}
        {(summary.active_sessions || []).length > 0 && (
          <div>
            <p className="text-xs text-gray-500 font-medium mb-2">Currently Active</p>
            <div className="space-y-2">
              {summary.active_sessions.map((s) => (
                <div key={s.id} className="flex items-center justify-between border-l-4 border-emerald-500 bg-emerald-50 rounded-r px-3 py-2 text-sm" data-testid={`time-log-active-session-${s.id}`}>
                  <div>
                    <span className="font-medium">{s.technician_name}</span>
                    <Badge className={`ml-2 ${TIME_LOG_ACTIVITY_TYPES[s.activity_type]?.color || 'bg-gray-100 text-gray-800'}`}>
                      {TIME_LOG_ACTIVITY_TYPES[s.activity_type]?.label || s.activity_type}
                    </Badge>
                  </div>
                  <span className="text-gray-600">Started {new Date(s.started_at).toLocaleString('en-IN')}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Full Activity Log */}
        {allSessions.length > 0 && (
          <div>
            <p className="text-xs text-gray-500 font-medium mb-2">Full Activity Log ({allSessions.length} session{allSessions.length !== 1 ? 's' : ''})</p>
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {allSessions.map((s) => (
                <div key={s.id} className="border rounded px-3 py-2 text-sm" data-testid={`time-log-session-${s.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{s.technician_name}</span>
                      <Badge className={TIME_LOG_ACTIVITY_TYPES[s.activity_type]?.color || 'bg-gray-100 text-gray-800'}>
                        {TIME_LOG_ACTIVITY_TYPES[s.activity_type]?.label || s.activity_type}
                      </Badge>
                      <Badge className={TIME_LOG_STATUS_STYLES[s.status] || 'bg-gray-100 text-gray-700'}>
                        {s.status}
                      </Badge>
                    </div>
                    <span className="font-medium text-gray-700">{formatMinutes(s.duration_minutes)}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(s.started_at).toLocaleString('en-IN')}
                    {s.ended_at ? ` → ${new Date(s.ended_at).toLocaleString('en-IN')}` : ' → in progress'}
                    {s.break_time_minutes > 0 ? ` · Break: ${formatMinutes(s.break_time_minutes)}` : ''}
                  </p>
                  {s.notes && (
                    <p className="text-xs text-gray-600 mt-1 italic">"{s.notes}"</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const JobCardView = ({ jobCard }) => {
  const branding = useGarageBrandingFull();
  const [customerDetails, setCustomerDetails] = useState(null);
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const handlePrint = () => {
    // Ensure the print dialog shows properly
    setTimeout(() => {
      window.print();
    }, 100);
  };

  // Fetch customer and vehicle details
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        // Fetch customer details if customer_id exists
        if (jobCard.customer_id) {
          try {
            const customerResponse = await axios.get(`${API}/customers/${jobCard.customer_id}`);
            setCustomerDetails(customerResponse.data);
          } catch (customerError) {
            console.warn('Customer not found by ID, using direct fields:', customerError);
            // Fallback to direct customer fields if customer record is deleted
            if (jobCard.customer_name) {
              setCustomerDetails({
                name: jobCard.customer_name,
                contact: jobCard.customer_contact,
                location: jobCard.customer_location
              });
            }
          }
        } else if (jobCard.customer_name) {
          // Use direct customer fields if no customer_id
          setCustomerDetails({
            name: jobCard.customer_name,
            contact: jobCard.customer_contact,
            location: jobCard.customer_location
          });
        }

        // Fetch vehicle details if vehicle_id exists
        if (jobCard.vehicle_id) {
          try {
            const vehicleResponse = await axios.get(`${API}/vehicles/${jobCard.vehicle_id}`);
            setVehicleDetails(vehicleResponse.data);
          } catch (vehicleError) {
            console.warn('Vehicle not found by ID, using direct fields:', vehicleError);
            // Fallback to direct vehicle fields if vehicle record is deleted
            if (jobCard.vehicle_make || jobCard.vin) {
              setVehicleDetails({
                make: jobCard.vehicle_make,
                model: jobCard.vehicle_model,
                series: jobCard.vehicle_series,
                year: jobCard.year,
                license_plate: jobCard.vehicle_reg,
                vin: jobCard.vin,
                fuel_type: jobCard.fuel_type,
                transmission_code: jobCard.transmission_code,
                mileage: jobCard.mileage
              });
            }
          }
        } else if (jobCard.vehicle_make || jobCard.vin) {
          // Use direct vehicle fields if no vehicle_id
          setVehicleDetails({
            make: jobCard.vehicle_make,
            model: jobCard.vehicle_model,
            series: jobCard.vehicle_series,
            year: jobCard.year,
            license_plate: jobCard.vehicle_reg,
            vin: jobCard.vin,
            fuel_type: jobCard.fuel_type,
            transmission_code: jobCard.transmission_code,
            mileage: jobCard.mileage
          });
        }
      } catch (error) {
        console.error('Error fetching job card details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [jobCard.customer_id, jobCard.vehicle_id, jobCard.customer_name, jobCard.vehicle_make]);

  // Calculate totals
  const laborCost = (jobCard.actual_hours || jobCard.estimated_hours || 0) * (jobCard.labor_rate || 0);
  const materialsCost = jobCard.parts_used?.reduce((sum, item) => sum + (item.total_cost || 0), 0) || 0;
  const totalCost = laborCost + materialsCost;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      case 'assigned': return 'bg-yellow-100 text-yellow-800';
      case 'pending': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="job-card-view">
      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .job-card-view, .job-card-view * {
            visibility: visible;
          }
          .job-card-view {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>

      <div className="bg-white p-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{branding.businessName.toUpperCase()}</h1>
            <p className="text-gray-600">Job Card & Work Order</p>
            {(branding.phone || branding.email) && (
              <p className="text-sm text-gray-500 mt-2">
                {[branding.phone && `Phone: ${branding.phone}`, branding.email && `Email: ${branding.email}`].filter(Boolean).join(' | ')}
              </p>
            )}
          </div>
          
          <div className="text-right">
            <h2 className="text-2xl font-bold text-orange-600 mb-2">JOB CARD</h2>
            <p className="text-lg font-semibold">#{jobCard.job_number || jobCard.id}</p>
            <p className="text-sm text-gray-600">
              Created: {new Date(jobCard.created_at).toLocaleDateString('en-IN')}
            </p>
            {jobCard.started_at && (
              <p className="text-sm text-gray-600">
                Started: {new Date(jobCard.started_at).toLocaleDateString('en-IN')}
              </p>
            )}
          </div>
        </div>

        {/* Job Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* Job Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Job Information</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Job Title:</span>
                <span className="font-medium">{jobCard.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(jobCard.status)}`}>
                  {jobCard.status ? jobCard.status.charAt(0).toUpperCase() + jobCard.status.slice(1) : 'Pending'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Priority:</span>
                <span className="font-medium capitalize">{jobCard.priority || 'Normal'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Assigned Technician:</span>
                <span className="font-medium">{jobCard.assigned_technician || 'Not Assigned'}</span>
              </div>
            </div>
          </div>

          {/* Vehicle Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Vehicle Information</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Make:</span>
                <span className="font-medium">
                  {vehicleDetails?.make || jobCard.vehicle_make || 'Unknown'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Model:</span>
                <span className="font-medium">
                  {vehicleDetails?.model || jobCard.vehicle_model || 'Unknown'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Series:</span>
                <span className="font-medium">
                  {vehicleDetails?.series || jobCard.vehicle_series || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Year:</span>
                <span className="font-medium">
                  {vehicleDetails?.year || jobCard.vehicle_year || jobCard.year || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">License Plate:</span>
                <span className="font-medium">
                  {vehicleDetails?.license_plate || jobCard.license_plate || jobCard.vehicle_reg || 'N/A'}
                </span>
              </div>
              {vehicleDetails?.vin && (
                <div className="flex justify-between">
                  <span className="text-gray-600">VIN:</span>
                  <span className="font-medium">{vehicleDetails.vin}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Work Description */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Work Description</h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-700">{jobCard.description || 'No description provided'}</p>
          </div>
        </div>

        {/* Labor Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* REMOVED: Labor Information section - not required */}

          {/* Parts/Materials Used */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Parts & Materials</h3>
            {jobCard.parts_used && jobCard.parts_used.length > 0 ? (
              <div className="space-y-2">
                {jobCard.parts_used.map((part, index) => (
                  <div key={part.id || `part-${index}`} className="flex justify-between text-sm bg-gray-50 p-2 rounded">
                    <span>{part.name || part.description} (x{part.quantity})</span>
                    {part.total_cost ? (
                      <span className="font-medium">₹{(part.total_cost || 0).toLocaleString('en-IN')}</span>
                    ) : (
                      <span className="text-xs text-gray-400 capitalize">{part.item_type === 'labor' ? 'service' : (part.item_type || 'part')}</span>
                    )}
                  </div>
                ))}
                {materialsCost > 0 && (
                  <div className="flex justify-between border-t pt-2 font-semibold">
                    <span className="text-gray-900">Materials Total:</span>
                    <span className="text-gray-900">₹{materialsCost.toLocaleString('en-IN')}</span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No parts/materials used</p>
            )}
          </div>
        </div>

        {/* Customer Information */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Name:</span>
              <span className="font-medium">
                {customerDetails?.name || jobCard.customer_name || 'Unknown Customer'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Phone:</span>
              <span className="font-medium">
                {customerDetails?.contact || customerDetails?.phone || jobCard.customer_contact || jobCard.customer_phone || 'No contact'}
              </span>
            </div>
            {(customerDetails?.email || customerDetails?.location || jobCard.customer_location) && (
              <>
                {customerDetails?.email && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium">{customerDetails.email}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Location:</span>
                  <span className="font-medium">
                    {customerDetails?.location || jobCard.customer_location || 'N/A'}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Total Cost Summary */}
        <div className="border-t-2 border-gray-300 pt-6 mb-8">
          <div className="flex justify-end">
            <div className="w-64">
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Labor Cost:</span>
                <span className="font-medium">₹{laborCost.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Materials Cost:</span>
                <span className="font-medium">₹{materialsCost.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between py-3 border-t-2 border-gray-900 bg-gray-50 px-4">
                <span className="text-lg font-bold text-gray-900">Total Cost:</span>
                <span className="text-lg font-bold text-orange-600">₹{totalCost.toLocaleString('en-IN')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 border-t border-gray-200 pt-4">
          <p>{branding.businessName} - Quality Automotive Service</p>
          {(branding.phone || branding.email) && (
            <p className="mt-1">For any queries, please contact us at the above phone number or email.</p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center space-x-4 mt-8 no-print">
          <Button onClick={handlePrint} className="bg-orange-600 hover:bg-orange-700">
            <Download className="h-4 w-4 mr-2" />
            Print Job Card
          </Button>
        </div>
      </div>
    </div>
  );
};

// JobCardsSection component removed completely to test form clearing issues

// ServicesSection removed - duplicate exists later in file

const JobCardItemsManager = ({ items, onItemsChange }) => {
  const [currentItem, setCurrentItem] = useState({
    description: '',
    item_type: 'part',
    quantity: 1,
    unit: 'pcs',
    unit_cost: '',
    part_number: '',
    supplier: ''
  });

  const addItem = () => {
    if (!currentItem.description.trim()) {
      toast.error("Please enter item description");
      return;
    }

    const newItem = {
      ...currentItem,
      id: Date.now().toString(),
      quantity: parseFloat(currentItem.quantity) || 1,
      unit_cost: parseFloat(currentItem.unit_cost) || 0,
      total_cost: (parseFloat(currentItem.quantity) || 1) * (parseFloat(currentItem.unit_cost) || 0),
      added_at: new Date().toISOString()
    };

    onItemsChange([...items, newItem]);
    
    // Reset form
    setCurrentItem({
      description: '',
      item_type: 'part',
      quantity: 1,
      unit: 'pcs',
      unit_cost: '',
      part_number: '',
      supplier: ''
    });
  };

  const removeItem = (itemId) => {
    onItemsChange(items.filter(item => item.id !== itemId));
  };

  const getTotalCost = () => {
    return items.reduce((sum, item) => sum + (item.total_cost || 0), 0);
  };

  return (
    <div className="space-y-4">
      {/* Add New Item Form */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Add New Item</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Item Type</Label>
              <Select value={currentItem.item_type} onValueChange={(value) => setCurrentItem({...currentItem, item_type: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="part">Spare Part</SelectItem>
                  <SelectItem value="labor">Labor/Service</SelectItem>
                  <SelectItem value="fuel">Fuel/Oil</SelectItem>
                  <SelectItem value="consumable">Consumable</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description *</Label>
              <Input
                value={currentItem.description}
                onChange={(e) => setCurrentItem({...currentItem, description: e.target.value})}
                placeholder="e.g., Engine Oil 5W-30"
              />
            </div>
            <div>
              <Label>Quantity</Label>
              <Input
                type="number"
                value={currentItem.quantity}
                onChange={(e) => setCurrentItem({...currentItem, quantity: e.target.value})}
                min="0"
                step="0.1"
              />
            </div>
            <div>
              <Label>Unit</Label>
              <Select value={currentItem.unit} onValueChange={(value) => setCurrentItem({...currentItem, unit: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pcs">Pieces</SelectItem>
                  <SelectItem value="liters">Liters</SelectItem>
                  <SelectItem value="kg">Kilograms</SelectItem>
                  <SelectItem value="hours">Hours</SelectItem>
                  <SelectItem value="meters">Meters</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Unit Cost (₹)</Label>
              <Input
                type="number"
                value={currentItem.unit_cost}
                onChange={(e) => setCurrentItem({...currentItem, unit_cost: e.target.value})}
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <Label>Part Number</Label>
              <Input
                value={currentItem.part_number}
                onChange={(e) => setCurrentItem({...currentItem, part_number: e.target.value})}
                placeholder="Optional part number"
              />
            </div>
            <div>
              <Label>Supplier</Label>
              <Input
                value={currentItem.supplier}
                onChange={(e) => setCurrentItem({...currentItem, supplier: e.target.value})}
                placeholder="Supplier name"
              />
            </div>
          </div>

          <Button onClick={addItem} size="sm" className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-3 w-3 mr-1" />
            Add Item
          </Button>
        </CardContent>
      </Card>

      {/* Items List */}
      {items.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex justify-between items-center">
              Added Items ({items.length})
              <Badge variant="outline">Total: ₹{getTotalCost().toFixed(2)}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {items.map((item) => (
                <div key={item.id} className="flex justify-between items-center p-3 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-xs">{item.item_type}</Badge>
                      <span className="font-medium">{item.description}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {item.quantity} {item.unit} × ₹{item.unit_cost} = ₹{item.total_cost.toFixed(2)}
                      {item.part_number && ` • Part: ${item.part_number}`}
                      {item.supplier && ` • Supplier: ${item.supplier}`}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => removeItem(item.id)}
                    className="text-red-600 border-red-600 hover:bg-red-50"
                  >
                    <Trash className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Status Workflow Component with proper transitions
// Document Management Component

// Document Management Component

const JobCardDocumentManager = ({ documents, onDocumentsChange }) => {
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (event, type) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const newDocuments = [];
      
      for (const file of files) {
        // Convert to base64 for storage
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });

        const document = {
          id: Date.now().toString() + Math.random(),
          type: type,
          filename: file.name,
          size: file.size,
          uploaded_at: new Date().toISOString(),
          data: base64
        };

        newDocuments.push(document);
      }

      onDocumentsChange([...documents, ...newDocuments]);
      toast.success(`${files.length} file(s) uploaded successfully!`);
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const removeDocument = (docId) => {
    onDocumentsChange(documents.filter(doc => doc.id !== docId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4">
      {/* Upload Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Vehicle Photos</Label>
          <div className="mt-1">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileUpload(e, 'photo')}
              className="hidden"
              id="photo-upload"
              disabled={uploading}
            />
            <Label
              htmlFor="photo-upload"
              className="flex items-center justify-center w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
            >
              <Camera className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload Vehicle Photos'}
            </Label>
          </div>
        </div>

        <div>
          <Label>Scanning Reports</Label>
          <div className="mt-1">
            <input
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.txt"
              onChange={(e) => handleFileUpload(e, 'report')}
              className="hidden"
              id="report-upload"
              disabled={uploading}
            />
            <Label
              htmlFor="report-upload"
              className="flex items-center justify-center w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
            >
              <FileText className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload Scan Reports'}
            </Label>
          </div>
        </div>
      </div>

      {/* Uploaded Documents List */}
      {documents.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Uploaded Documents ({documents.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {documents.map((doc) => (
                <div key={doc.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                  <div className="flex items-center gap-2">
                    {doc.type === 'photo' ? (
                      <Camera className="h-4 w-4 text-blue-600" />
                    ) : (
                      <FileText className="h-4 w-4 text-green-600" />
                    )}
                    <div>
                      <p className="text-sm font-medium">{doc.filename}</p>
                      <p className="text-xs text-gray-600">
                        {formatFileSize(doc.size)} • {new Date(doc.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => removeDocument(doc.id)}
                    className="text-red-600 border-red-600 hover:bg-red-50"
                  >
                    <Trash className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Job Card Details View Component

const JobCardDetailsView = ({ jobCard, customers, vehicles, onClose }) => {
  const statusConfig = {
    "created": { color: "bg-gray-100 text-gray-800", icon: FileText, label: "Created" },
    "assigned": { color: "bg-blue-100 text-blue-800", icon: Users, label: "Assigned" },
    "in-progress": { color: "bg-yellow-100 text-yellow-800", icon: Wrench, label: "In Progress" },
    "waiting-parts": { color: "bg-purple-100 text-purple-800", icon: Package, label: "Waiting Parts" },
    "completed": { color: "bg-green-100 text-green-800", icon: CheckCircle, label: "Completed" },
    "invoiced": { color: "bg-emerald-100 text-emerald-800", icon: DollarSign, label: "Invoiced" },
    "rework": { color: "bg-rose-100 text-rose-800", icon: RotateCcw, label: "Rework" }
  };

  const statusInfo = statusConfig[jobCard.status] || statusConfig.created;
  const StatusIcon = statusInfo.icon;

  const getVehicleDisplay = () => {
    if (jobCard.vehicle_make && jobCard.vehicle_model) {
      return `${jobCard.year ? jobCard.year + ' ' : ''}${jobCard.vehicle_make} ${jobCard.vehicle_model}`;
    }
    const vehicle = vehicles.find(v => v.id === jobCard.vehicle_id);
    return vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : 'Unknown Vehicle';
  };

  const getCustomerDisplay = () => {
    if (jobCard.customer_name) {
      return jobCard.customer_name;
    }
    const customer = customers.find(c => c.id === jobCard.customer_id);
    return customer ? customer.name : 'Unknown Customer';
  };

  return (
    <div className="space-y-6">
      <DialogHeader>
        <div className="flex items-start justify-between">
          <div>
            <DialogTitle className="text-xl">{jobCard.title || 'Untitled Job'}</DialogTitle>
            <DialogDescription>
              Job Card #{jobCard.job_number}
            </DialogDescription>
          </div>
          <Badge className={`${statusInfo.color} flex items-center gap-1`}>
            <StatusIcon className="h-3 w-3" />
            {statusInfo.label}
          </Badge>
        </div>
      </DialogHeader>

      <div className="grid grid-cols-1 gap-6">
        {/* Job Card Header Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-medium text-gray-800">📋 Job Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-gray-500 font-medium">Job Number</p>
                <p className="text-sm font-bold text-blue-700">{jobCard.job_number || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Assigned Technician</p>
                <p className="text-sm font-medium">{jobCard.assigned_technician || 'Unassigned'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Priority Level</p>
                <Badge variant="outline" className="capitalize">
                  {jobCard.priority || 'medium'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detailed Customer Information */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-medium text-gray-800">👤 Customer Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 font-medium">Customer Name</p>
                <p className="text-lg font-bold text-gray-900">{jobCard.customer_name || getCustomerDisplay()}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Contact Number</p>
                <p className="text-sm font-medium text-blue-600">{jobCard.customer_contact || jobCard.customer_phone || 'N/A'}</p>
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">Address / Location</p>
              <p className="text-sm">{jobCard.customer_location || jobCard.customer_address || 'N/A'}</p>
            </div>
            {jobCard.customer_email && (
              <div>
                <p className="text-xs text-gray-500 font-medium">Email</p>
                <p className="text-sm text-blue-600">{jobCard.customer_email}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detailed Vehicle Information - COMPLETE */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-medium text-gray-800">🚗 Complete Vehicle Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Primary Vehicle Info */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Make & Model</p>
                  <p className="text-xl font-bold text-blue-800">{getVehicleDisplay()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Year</p>
                  <p className="text-lg font-semibold text-gray-900">{jobCard.year || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Color</p>
                  <p className="text-sm font-medium">{jobCard.color || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Registration & Identification */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded p-3">
                <p className="text-xs text-gray-500 font-medium">License Plate</p>
                <p className="text-lg font-bold text-blue-700">{jobCard.vehicle_reg || 'N/A'}</p>
              </div>
              <div className="border rounded p-3">
                <p className="text-xs text-gray-500 font-medium">VIN Number</p>
                <p className="text-sm font-mono text-xs break-all">{jobCard.vin || 'N/A'}</p>
              </div>
            </div>

            {/* Technical Specifications */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Technical Specifications</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Engine</p>
                  <p className="text-sm font-medium">{jobCard.engine_model || jobCard.engine_code || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Transmission No</p>
                  <p className="text-sm font-medium">{jobCard.transmission_model || jobCard.transmission_code || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Fuel Type</p>
                  <p className="text-sm font-medium capitalize">{jobCard.fuel_type || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Mileage & Condition */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded p-3">
                <p className="text-xs text-gray-500 font-medium">Current Mileage</p>
                <p className="text-lg font-bold text-green-600">{jobCard.mileage ? `${jobCard.mileage.toLocaleString()} km` : 'N/A'}</p>
              </div>
              <div className="border rounded p-3">
                <p className="text-xs text-gray-500 font-medium">Vehicle Series</p>
                <p className="text-sm font-medium">{jobCard.vehicle_series || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Right Column - Time & Cost */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Time & Labor</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-gray-500">Assigned Technician</p>
                <p className="font-medium">{jobCard.assigned_technician === 'unassigned' || !jobCard.assigned_technician ? 'Unassigned' : jobCard.assigned_technician.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
              </div>
              {/* REMOVED: Time tracking fields - not required */}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Dates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-gray-500">Created</p>
                <p className="text-sm">{new Date(jobCard.created_at).toLocaleString('en-IN')}</p>
              </div>
              {jobCard.started_at && (
                <div>
                  <p className="text-xs text-gray-500">Started</p>
                  <p className="text-sm">{new Date(jobCard.started_at).toLocaleString('en-IN')}</p>
                </div>
              )}
              {jobCard.completed_at && (
                <div>
                  <p className="text-xs text-gray-500">Completed</p>
                  <p className="text-sm">{new Date(jobCard.completed_at).toLocaleString('en-IN')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Time Tracking Log - full activity details */}
      <TimeTrackingLogSection jobCardId={jobCard.id} />

      {/* Activity Log - status changes, technician assignment, items added/removed, hold/resume */}
      <JobCardActivityLogSection activityLog={jobCard.activity_log} />

      {/* Vehicle Problems - Complete Display */}
      {(jobCard.issue_title || (jobCard.vehicle_issues && jobCard.vehicle_issues.length > 0)) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">🔧 Vehicle Problems</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Main Problem */}
            {jobCard.issue_title && (
              <div className="border-l-4 border-blue-500 pl-4">
                <p className="text-xs text-gray-500">Main Problem</p>
                <p className="font-medium text-lg">{jobCard.issue_title}</p>
                {jobCard.issue_category && (
                  <p className="text-sm text-gray-600 bg-gray-100 inline-block px-2 py-1 rounded mt-1">
                    Category: {jobCard.issue_category}
                  </p>
                )}
              </div>
            )}
            
            {/* Vehicle Issues from New Form */}
            {jobCard.vehicle_issues && jobCard.vehicle_issues.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 font-medium mb-2">Vehicle Issues</p>
                <div className="space-y-3">
                  {jobCard.vehicle_issues.map((issue, index) => (
                    <div key={index} className="border-l-4 border-orange-400 pl-4 py-3 bg-orange-50 rounded-r">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-lg">{issue.title}</p>
                          {issue.category && (
                            <p className="text-sm text-gray-600 bg-orange-200 inline-block px-2 py-1 rounded mt-1">
                              📋 {capitalizeWords(issue.category)}{issue.subcategory ? ` → ${capitalizeWords(issue.subcategory)}` : ''}
                            </p>
                          )}
                          {issue.dtc && (
                            <p className="text-sm text-gray-700 bg-yellow-200 inline-block px-2 py-1 rounded mt-1 ml-2">
                              🔍 DTC: {issue.dtc}
                            </p>
                          )}
                        </div>
                      </div>
                      {issue.id && (
                        <IssueMediaAttachments jobCardId={jobCard.id} issueId={issue.id} readOnly />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Legacy Additional Problems (for backward compatibility) */}
            {jobCard.additional_issues && jobCard.additional_issues.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 font-medium mb-2">Additional Customer Concerns</p>
                <div className="space-y-2">
                  {jobCard.additional_issues.map((issue, index) => (
                    <div key={index} className="border-l-4 border-orange-400 pl-4 py-2 bg-orange-50 rounded-r">
                      <p className="font-medium">{issue.title}</p>
                      {issue.category && (
                        <p className="text-sm text-gray-600 bg-orange-200 inline-block px-2 py-1 rounded mt-1">
                          {capitalizeWords(issue.category)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {jobCard.issue_symptoms && (
              <div>
                <p className="text-xs text-gray-500">Symptoms</p>
                <p className="text-sm">{jobCard.issue_symptoms}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              {jobCard.issue_category && (
                <div>
                  <p className="text-xs text-gray-500">Category</p>
                  <Badge variant="outline" className="capitalize">
                    {jobCard.issue_category}
                  </Badge>
                </div>
              )}
              {jobCard.issue_priority && (
                <div>
                  <p className="text-xs text-gray-500">Problem Priority</p>
                  <Badge variant="outline" className="capitalize">
                    {jobCard.issue_priority}
                  </Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Diagnostic Scanning Reports */}
      {(jobCard.scanning_reports && jobCard.scanning_reports.length > 0) ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-medium text-gray-800">Diagnostic Scanning Reports ({jobCard.scanning_reports.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {jobCard.scanning_reports.map((report, index) => (
                <div key={index} className="border rounded-lg p-4 bg-blue-50" data-testid={`scanning-report-${index}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 font-medium">Report File</p>
                      <p className="text-sm font-bold text-blue-700 truncate">{report.filename || `scanning_report_${index + 1}.pdf`}</p>
                      {report.uploaded_at && (
                        <p className="text-xs text-gray-500 mt-0.5">{new Date(report.uploaded_at).toLocaleDateString()}</p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-blue-100 hover:bg-blue-200 text-blue-700 border-blue-300"
                        onClick={() => window.open(`${API}/jobcards/${jobCard.id}/reports/${index}`, '_blank')}
                        data-testid={`scanning-report-view-${index}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-green-100 hover:bg-green-200 text-green-700 border-green-300"
                        onClick={() => window.open(`${API}/jobcards/${jobCard.id}/reports/${index}?download=true`, '_blank')}
                        data-testid={`scanning-report-download-${index}`}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : jobCard.scanning_report_path ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-medium text-gray-800">Diagnostic Scanning Report</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg p-4 bg-blue-50">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 font-medium">Report File</p>
                  <p className="text-sm font-bold text-blue-700 truncate">{jobCard.scanning_report_filename || 'scanning_report.pdf'}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-blue-100 hover:bg-blue-200 text-blue-700 border-blue-300"
                    onClick={() => window.open(`${API}/jobcards/${jobCard.id}/reports/0`, '_blank')}
                    data-testid="scanning-report-view-0"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-green-100 hover:bg-green-200 text-green-700 border-green-300"
                    onClick={() => window.open(`${API}/jobcards/${jobCard.id}/download-report`, '_blank')}
                    data-testid="scanning-report-download-0"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : jobCard.scanning_report_filename ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-medium text-gray-800">Diagnostic Scanning Report</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-amber-700 font-medium">File Name Recorded</p>
                  <p className="text-sm font-bold text-amber-800">{jobCard.scanning_report_filename}</p>
                  <p className="text-xs text-amber-600 mt-1">No PDF was uploaded. Open Edit Job Card to attach the file.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Vehicle Photos */}
      {((jobCard.vehicle_photos && jobCard.vehicle_photos.length > 0) || jobCard.photo_filenames) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-medium text-gray-800">Vehicle Photos</CardTitle>
          </CardHeader>
          <CardContent>
            {jobCard.vehicle_photos && jobCard.vehicle_photos.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {jobCard.vehicle_photos.map((photo, index) => (
                  <div key={index} className="border rounded-lg overflow-hidden bg-gray-50" data-testid={`vehicle-photo-${index}`}>
                    <img 
                      src={`${API}/jobcards/${jobCard.id}/photos/${index}`}
                      alt={photo.filename}
                      className="w-full h-40 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => window.open(`${API}/jobcards/${jobCard.id}/photos/${index}`, '_blank')}
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                    <div className="hidden items-center justify-center h-40 bg-gray-100 text-gray-400">
                      <Camera className="h-8 w-8" />
                    </div>
                    <div className="p-2 text-xs text-gray-600 truncate">{photo.filename}</div>
                    <div className="flex gap-2 px-2 pb-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 bg-blue-100 hover:bg-blue-200 text-blue-700 border-blue-300"
                        onClick={() => window.open(`${API}/jobcards/${jobCard.id}/photos/${index}`, '_blank')}
                        data-testid={`vehicle-photo-view-${index}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 bg-green-100 hover:bg-green-200 text-green-700 border-green-300"
                        onClick={() => window.open(`${API}/jobcards/${jobCard.id}/photos/${index}?download=true`, '_blank')}
                        data-testid={`vehicle-photo-download-${index}`}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {jobCard.photo_filenames.split(',').filter(f => f.trim()).map((filename, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-green-50 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <Camera className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium text-green-800">{filename.trim()}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-green-100 hover:bg-green-200 text-green-700 border-green-300"
                      onClick={() => window.open(`${API}/jobcards/${jobCard.id}/photos/${index}`, '_blank')}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      View / Download
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Vehicle Videos */}
      {((jobCard.vehicle_videos && jobCard.vehicle_videos.length > 0) || jobCard.video_filenames) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-medium text-gray-800">Vehicle Videos</CardTitle>
          </CardHeader>
          <CardContent>
            {jobCard.vehicle_videos && jobCard.vehicle_videos.length > 0 ? (
              <div className="space-y-3">
                {jobCard.vehicle_videos.map((video, index) => (
                  <div key={index} className="border rounded-lg overflow-hidden bg-purple-50">
                    <video 
                      controls
                      className="w-full max-h-64"
                      preload="metadata"
                    >
                      <source src={`${API}/jobcards/${jobCard.id}/videos/${index}`} type="video/mp4" />
                      Your browser does not support video playback.
                    </video>
                    <div className="p-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-purple-800">{video.filename}</p>
                        <p className="text-xs text-gray-500">
                          {video.size_bytes ? `${(video.size_bytes / (1024 * 1024)).toFixed(1)} MB` : ''}
                          {video.uploaded_at ? ` · ${new Date(video.uploaded_at).toLocaleDateString()}` : ''}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-blue-100 hover:bg-blue-200 text-blue-700 border-blue-300"
                        onClick={() => window.open(`${API}/jobcards/${jobCard.id}/videos/${index}`, '_blank')}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-purple-100 hover:bg-purple-200 text-purple-700 border-purple-300"
                        onClick={() => window.open(`${API}/jobcards/${jobCard.id}/videos/${index}`, '_blank')}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : jobCard.video_filenames ? (
              <div className="space-y-2">
                {jobCard.video_filenames.split(',').filter(f => f.trim()).map((filename, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-purple-50 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-purple-600" />
                      <span className="text-sm font-medium text-purple-800">{filename.trim()}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-purple-100 hover:bg-purple-200 text-purple-700 border-purple-300"
                      onClick={() => window.open(`${API}/jobcards/${jobCard.id}/videos/${index}`, '_blank')}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Parts & Services Used */}
      {jobCard.parts_used && jobCard.parts_used.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Parts & Services Used</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="view-parts-used-list">
              {jobCard.parts_used.map((item, index) => (
                <div key={item.id || index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                  <div>
                    <p className="text-sm font-medium">{item.description || item.name}</p>
                    <p className="text-xs text-gray-600">
                      {item.quantity} {item.unit}
                      {item.part_number ? ` • Part: ${item.part_number}` : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    {item.total_cost ? (
                      <p className="text-sm font-medium">₹{item.total_cost.toFixed(0)}</p>
                    ) : (
                      <Badge variant="outline" className="text-xs capitalize">
                        {item.item_type === 'labor' ? 'service' : (item.item_type || 'part')}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cost Summary */}
      {jobCard.total_cost && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Cost Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center">
              <p className="font-medium">Total Cost</p>
              <p className="text-lg font-bold text-green-600">₹{jobCard.total_cost.toFixed(0)}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notes */}
      {jobCard.notes && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{jobCard.notes}</p>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button onClick={onClose} variant="outline">
          Close
        </Button>
      </div>
    </div>
  );
};

const AddJobCardForm = ({ onJobCardAdded, customers, vehicles, employees = [], onCancel }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [usedItems, setUsedItems] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [vehicleSearchTerm, setVehicleSearchTerm] = useState('');
  const [showVehicleSuggestions, setShowVehicleSuggestions] = useState(false);

  // Enhanced Vehicle Problems fetched from /api/vehicle-issues (Enhanced Vehicle Problems Database)
  const [enhancedProblems, setEnhancedProblems] = useState([]);
  useEffect(() => {
    const fetchEnhancedProblems = async () => {
      try {
        const response = await axios.get(`${API}/vehicle-issues`);
        setEnhancedProblems(response.data || []);
      } catch (error) {
        console.error('Failed to fetch enhanced vehicle problems:', error);
        setEnhancedProblems([]);
      }
    };
    fetchEnhancedProblems();
  }, []);

  // Store actual file objects for upload after job card creation
  const pendingVideoFiles = React.useRef([]);
  const pendingPhotoFiles = React.useRef([]);
  const pendingPdfFile = React.useRef(null);

  // Debug logging for employees data
  console.log('AddJobCardForm - employees data:', employees);
  console.log('AddJobCardForm - available technicians:', employees.filter(emp => 
    emp.position === 'technician' || 
    emp.position === 'senior_technician' || 
    emp.position === 'junior_technician' ||
    emp.position === 'senior_mechanic' ||
    emp.position === 'junior_mechanic'
  ));

  // React Hook Form setup
  const { 
    register, 
    handleSubmit, 
    formState: { errors }, 
    setValue, 
    watch, 
    getValues, 
    reset,
    control 
  } = useForm({
    defaultValues: {
      job_number: '',
      vehicle_id: '',
      customer_id: '',
      assigned_technician: '',
      priority: 'medium',
      customer_name: '',
      customer_location: '',
      customer_contact: '',
      customer_email: '',
      vehicle_make: '',
      vehicle_model: '',
      vehicle_series: '',
      year: '',
      color: '',
      vin: '',
      engine_model: '',
      transmission_model: '',
      fuel_type: '',
      vehicle_reg: '',
      mileage: '',
      remarks: '',
      scanning_report_filename: '',
      photo_filenames: '',
      video_filenames: '',
      video_files: null,
      // Vehicle Issues integrated with React Hook Form
      vehicle_issues: [{ 
        id: 1, 
        title: '', 
        category: '', 
        subcategory: '',
        manualTitle: false
      }],
      // Standalone DTC Codes section
      dtc_codes: [{ 
        id: 1, 
        code: '', 
        description: '', 
        manual: false 
      }]
    }
  });

  // useFieldArray for Vehicle Issues - prevents re-render on keystroke
  const { fields: issueFields, append: appendIssue, remove: removeIssueAt, update: updateIssueAt } = useFieldArray({
    control,
    name: 'vehicle_issues'
  });

  // useFieldArray for DTC Codes - prevents re-render on keystroke
  const { fields: dtcFields, append: appendDtc, remove: removeDtcAt, update: updateDtcAt } = useFieldArray({
    control,
    name: 'dtc_codes'
  });

  // Wrapper functions that match existing API
  const addVehicleIssue = useCallback(() => {
    appendIssue({ id: Date.now(), title: '', category: '', subcategory: '', manualTitle: false });
  }, [appendIssue]);

  const removeVehicleIssue = useCallback((id) => {
    const idx = issueFields.findIndex(f => f.id === id);
    if (idx >= 0 && issueFields.length > 1) removeIssueAt(idx);
  }, [issueFields, removeIssueAt]);

  const updateVehicleIssue = useCallback((id, field, value) => {
    const idx = issueFields.findIndex(f => f.id === id);
    if (idx >= 0) {
      const current = issueFields[idx];
      updateIssueAt(idx, { ...current, [field]: value });
    }
  }, [issueFields, updateIssueAt]);

  const addDtcCode = useCallback(() => {
    appendDtc({ id: Date.now(), code: '', description: '', manual: false });
  }, [appendDtc]);

  const removeDtcCode = useCallback((id) => {
    const idx = dtcFields.findIndex(f => f.id === id);
    if (idx >= 0 && dtcFields.length > 1) removeDtcAt(idx);
  }, [dtcFields, removeDtcAt]);

  const updateDtcCode = useCallback((id, field, value) => {
    const idx = dtcFields.findIndex(f => f.id === id);
    if (idx >= 0) {
      const current = dtcFields[idx];
      updateDtcAt(idx, { ...current, [field]: value });
    }
  }, [dtcFields, updateDtcAt]);

  // Watch specific fields for reactive updates
  const watchedCustomerName = watch('customer_name');
  const watchedVehicleId = watch('vehicle_id');
  const watchedCustomerId = watch('customer_id');

  // Autosave draft (create only)
  const jcValues = watch();
  const jcDraftHasContent =
    (jcValues.customer_name || '').trim() !== '' ||
    (jcValues.customer_contact || '').trim() !== '' ||
    (jcValues.vehicle_make || '') !== '' ||
    (jcValues.vehicle_reg || '').trim() !== '' ||
    (jcValues.title || '').trim() !== '' ||
    (jcValues.description || '').trim() !== '' ||
    (Array.isArray(jcValues.vehicle_issues) && jcValues.vehicle_issues.some((i) => i && (i.title || i.category || i.dtc)));
  const { draftRestored, clearDraft, dismissBanner } = useFormDraft({
    storageKey: 'draft:jobcard-create',
    hasContent: jcDraftHasContent,
    data: jcValues,
    onRestore: (d) => { if (d) reset(d); },
  });
  const discardJobCardDraft = () => {
    clearDraft();
    reset();
  };

  // Customer selection handler
  const handleCustomerSelection = useCallback((customerId) => {
    if (customerId) {
      const customer = customers.find(c => c.id === customerId);
      if (customer) {
        setSelectedCustomer(customer);
        setValue('customer_id', customerId);
        // Always fill customer fields
        setValue('customer_name', customer.name || '');
        setValue('customer_location', customer.location || customer.address || '');
        setValue('customer_contact', customer.contact || customer.phone || '');
        setValue('customer_email', customer.email || '');
        
        // Auto-fill vehicle info from customer's first vehicle
        const customerVehicles = vehicles.filter(v => v && v.customer_id === customerId);
        if (customerVehicles.length > 0) {
          const vehicle = customerVehicles[0];
          setSelectedVehicle(vehicle);
          setValue('vehicle_id', vehicle.id || '');
          setValue('vehicle_make', vehicle.make || '');
          setValue('vehicle_model', vehicle.model || '');
          setValue('vehicle_series', vehicle.series || '');
          setValue('color', vehicle.color || '');
          setValue('vin', vehicle.vin || '');
          setValue('engine_model', vehicle.engine_no || '');
          setValue('transmission_model', vehicle.transmission || '');
          setValue('vehicle_reg', vehicle.license_plate || '');
          setValue('mileage', vehicle.mileage || '');
          setValue('year', vehicle.year || '');
          setValue('fuel_type', vehicle.fuel_type || '');
          toast.success(`Customer & vehicle selected: ${customer.name} - ${vehicle.license_plate || vehicle.make + ' ' + vehicle.model}`);
        } else {
          toast.success(`Customer selected: ${customer.name}`);
        }
      }
    } else {
      setSelectedCustomer(null);
      setValue('customer_id', '');
    }
  }, [customers, vehicles, setValue]);

  // Vehicle selection handler  
  const handleVehicleSelection = useCallback((vehicleId) => {
    if (vehicleId) {
      const vehicle = vehicles.find(v => v.id === vehicleId);
      if (vehicle) {
        setSelectedVehicle(vehicle);
        setValue('vehicle_id', vehicleId);
        
        // Only update empty fields to preserve user input
        const currentValues = getValues();
        if (!currentValues.vehicle_make) setValue('vehicle_make', vehicle.make || '');
        if (!currentValues.vehicle_model) setValue('vehicle_model', vehicle.model || '');
        if (!currentValues.vehicle_series) setValue('vehicle_series', vehicle.series || '');
        if (!currentValues.color) setValue('color', vehicle.color || '');
        if (!currentValues.vin) setValue('vin', vehicle.vin || '');
        if (!currentValues.engine_model) setValue('engine_model', vehicle.engine_no || '');
        if (!currentValues.transmission_model) setValue('transmission_model', vehicle.transmission || '');
        if (!currentValues.vehicle_reg) setValue('vehicle_reg', vehicle.license_plate || '');
        if (!currentValues.mileage) setValue('mileage', vehicle.mileage || '');
        
        toast.success(`Vehicle selected: ${vehicle.year} ${vehicle.make} ${vehicle.model} (${vehicle.license_plate})`);
      }
    } else {
      setSelectedVehicle(null);
      setValue('vehicle_id', '');
    }
  }, [vehicles, setValue, getValues]);

  // Filter customers based on search
  const filteredCustomers = customers.filter(customer => {
    if (!customerSearchTerm) return true;
    const term = customerSearchTerm.toLowerCase();
    return (customer.name || '').toLowerCase().includes(term) ||
           String(customer.contact || '').includes(customerSearchTerm) ||
           String(customer.phone || '').includes(customerSearchTerm) ||
           (customer.location || '').toLowerCase().includes(term) ||
           (customer.address || '').toLowerCase().includes(term);
  });

  // Vehicle filtering with search
  const filteredVehicles = useMemo(() => {
    if (!vehicleSearchTerm || !vehicleSearchTerm.trim()) return [];
    if (!vehicles || !Array.isArray(vehicles) || vehicles.length === 0) return [];
    
    const searchTerm = vehicleSearchTerm.toLowerCase().trim();
    
    return vehicles.filter(vehicle => {
      if (!vehicle || typeof vehicle !== 'object') return false;
      
      return (
        (vehicle.make && vehicle.make.toLowerCase().includes(searchTerm)) ||
        (vehicle.model && vehicle.model.toLowerCase().includes(searchTerm)) ||
        (vehicle.license_plate && vehicle.license_plate.toLowerCase().includes(searchTerm)) ||
        (vehicle.vin && vehicle.vin.toLowerCase().includes(searchTerm)) ||
        (vehicle.year && vehicle.year.toString().includes(searchTerm))
      );
    }).slice(0, 10); // Limit to 10 results
  }, [vehicles, vehicleSearchTerm]);

  // Dynamic vehicle data from backend API  
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableSeries, setAvailableSeries] = useState([]);
  const [vehicleDataLoading, setVehicleDataLoading] = useState(false);

  // Watch selected vehicle make and model to update options
  const watchedVehicleMake = watch('vehicle_make');
  const watchedVehicleModel = watch('vehicle_model');

  // Fetch vehicle makes from API
  const fetchVehicleMakes = useCallback(async () => {
    try {
      setVehicleDataLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/vehicle-data/makes`);
      const makes = (response.data.makes || []).filter(make => make && make.trim() !== '');
      setAvailableMakes(makes);
    } catch (error) {
      console.error('Error fetching vehicle makes:', error);
      // Fallback to basic makes if API fails
      setAvailableMakes(['Mercedes-Benz', 'BMW', 'Audi', 'Toyota', 'Honda', 'Other']);
    } finally {
      setVehicleDataLoading(false);
    }
  }, []);

  // Fetch vehicle models for selected make
  const fetchVehicleModels = useCallback(async (make) => {
    if (!make) {
      setAvailableModels([]);
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/vehicle-data/models/${encodeURIComponent(make)}`);
      const models = (response.data.models || []).filter(model => model && model.trim() !== '');
      setAvailableModels(models);
    } catch (error) {
      console.error('Error fetching vehicle models:', error);
      setAvailableModels([]);
    }
  }, []);

  // Fetch vehicle series for selected make and model
  const fetchVehicleSeries = useCallback(async (make, model) => {
    if (!make || !model) {
      setAvailableSeries([]);
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/vehicle-data/series/${encodeURIComponent(make)}/${encodeURIComponent(model)}`);
      const series = (response.data.series || []).filter(s => s && s.trim() !== '');
      setAvailableSeries(series);
    } catch (error) {
      console.error('Error fetching vehicle series:', error);
      setAvailableSeries([]);
    }
  }, []);

  // Initialize vehicle makes on component mount
  useEffect(() => {
    fetchVehicleMakes();
  }, [fetchVehicleMakes]);

  // Listen for vehicle data updates from "Add New Vehicle Data" feature
  useEffect(() => {
    const handleVehicleDataUpdate = () => {
      fetchVehicleMakes();
      if (watchedVehicleMake) {
        fetchVehicleModels(watchedVehicleMake);
      }
      if (watchedVehicleMake && watchedVehicleModel) {
        fetchVehicleSeries(watchedVehicleMake, watchedVehicleModel);
      }
    };

    window.addEventListener('vehicleDataUpdated', handleVehicleDataUpdate);
    return () => window.removeEventListener('vehicleDataUpdated', handleVehicleDataUpdate);
  }, [watchedVehicleMake, watchedVehicleModel, fetchVehicleMakes, fetchVehicleModels, fetchVehicleSeries]);

  // Fetch models when make changes
  useEffect(() => {
    fetchVehicleModels(watchedVehicleMake);
  }, [watchedVehicleMake, fetchVehicleModels]);

  // Fetch series when make or model changes
  useEffect(() => {
    fetchVehicleSeries(watchedVehicleMake, watchedVehicleModel);
  }, [watchedVehicleMake, watchedVehicleModel, fetchVehicleSeries]);

  // Subcategories mapping based on categories
  const getSubcategoriesByCategory = (category) => {
    const subcategoriesMap = {
      "Engine": [
        "Fuel System", "Ignition System", "Cooling System", "Lubrication", 
        "Exhaust System", "Turbo/Supercharger", "Engine Performance", "Engine Mechanical"
      ],
      "Transmission": [
        "Manual Transmission", "Automatic Transmission", "CVT", "Clutch System", 
        "Transmission Fluid", "Gearbox", "Differential"
      ],
      "Brakes": [
        "Brake Pads", "Brake Discs/Rotors", "Brake Fluid", "Brake Lines", 
        "ABS System", "Handbrake", "Brake Calipers", "Master Cylinder"
      ],
      "Electrical": [
        "Battery", "Alternator", "Starter", "Lighting", "Wiring", 
        "Dashboard", "Power Windows", "Central Locking", "ECU/PCM"
      ],
      "Suspension": [
        "Shock Absorbers", "Struts", "Springs", "Bushings", "Control Arms", 
        "Stabilizer Bar", "Ball Joints", "Suspension Bearings"
      ],
      "Steering": [
        "Power Steering", "Steering Wheel", "Steering Column", "Tie Rods", 
        "Steering Rack", "Steering Pump", "Steering Fluid"
      ],
      "AC/Climate": [
        "AC Compressor", "AC Condenser", "Evaporator", "Refrigerant", 
        "Blower Motor", "Climate Control", "Heater Core", "AC Filters"
      ],
      "Fuel System": [
        "Fuel Pump", "Fuel Injectors", "Fuel Filter", "Fuel Tank", 
        "Fuel Lines", "Fuel Rail", "Throttle Body", "Carbon Cleaning"
      ],
      "Exhaust": [
        "Muffler", "Catalytic Converter", "Exhaust Pipe", "Lambda Sensor", 
        "DPF Filter", "EGR Valve", "Exhaust Manifold"
      ],
      "Other": [
        "Body Work", "Interior", "Tires", "Windows", "Locks", 
        "General Maintenance", "Inspection", "Custom Work"
      ]
    };
    
    return subcategoriesMap[category] || [];
  };

  // Helpers to derive Category & Subcategory lists from the Enhanced Vehicle Problems DB
  const getDbCategories = () => {
    const cats = new Set();
    enhancedProblems.forEach(p => {
      if (p.category) cats.add(p.category);
    });
    return Array.from(cats).sort();
  };

  const getDbSubcategories = (category) => {
    if (!category) return [];
    const subs = new Set();
    enhancedProblems.forEach(p => {
      if (p.category === category && p.subcategory) subs.add(p.subcategory);
    });
    return Array.from(subs).sort();
  };

  // Get filtered issue titles from Enhanced Vehicle Problems Database ONLY
  // Filters by selected category + subcategory. Returns empty list if no DB matches.
  const getFilteredIssueTitles = (category, subcategory) => {
    const dbOptions = enhancedProblems
      .filter(p => {
        if (!p.title) return false;
        if (category && p.category !== category) return false;
        if (subcategory && p.subcategory && p.subcategory !== subcategory) return false;
        return true;
      })
      .map(p => ({
        value: p.title,
        label: p.is_solved ? `${p.title} ✓` : p.title,
        category: p.category || 'Other',
        subcategory: p.subcategory || '',
        source: 'enhanced_db'
      }));

    // De-duplicate by title (in case of duplicates in DB)
    const seen = new Set();
    return dbOptions.filter(o => {
      const key = o.value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // Form submit handler
  const onSubmit = async (data) => {
    if (!data.customer_name.trim()) {
      toast.error("Customer name is required");
      return;
    }

    setIsSubmitting(true);

    try {
      // Leave job_number empty so the backend assigns a short sequential
      // number (JC-<year>-NNN). Only a manually-entered value is kept.

      // Prepare job card data
      const jobCardData = {
        ...data,
        vehicle_issues: data.vehicle_issues?.filter(issue => issue.title || issue.category || issue.dtc) || [],
        parts_used: usedItems,
        created_at: new Date().toISOString(),
        status: "created"
      };

      // Remove file refs from data (not serializable)
      delete jobCardData.video_files;

      console.log('Submitting job card data:', jobCardData);

      // Submit to backend
      const response = await axios.post(`${API}/jobcards`, jobCardData);
      const createdJobCard = response.data;
      const jobCardId = createdJobCard.id;
      
      // Upload files after job card creation
      let uploadCount = 0;

      // Upload scanning report PDF
      if (pendingPdfFile.current) {
        const pdfs = Array.isArray(pendingPdfFile.current) ? pendingPdfFile.current : [pendingPdfFile.current];
        try {
          const formData = new FormData();
          pdfs.forEach(f => formData.append('files', f));
          await axios.post(`${API}/jobcards/${jobCardId}/upload-report`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          uploadCount += pdfs.length;
        } catch (err) {
          console.error('PDF upload failed:', err);
          toast.error('PDF upload failed - you can re-upload from Edit');
        }
      }

      // Upload photos
      if (pendingPhotoFiles.current.length > 0) {
        try {
          const formData = new FormData();
          pendingPhotoFiles.current.forEach(f => formData.append('files', f));
          await axios.post(`${API}/jobcards/${jobCardId}/upload-photos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          uploadCount += pendingPhotoFiles.current.length;
        } catch (err) {
          console.error('Photo upload failed:', err);
          toast.error('Photo upload failed - you can re-upload from Edit');
        }
      }

      // Upload videos
      if (pendingVideoFiles.current.length > 0) {
        try {
          toast.info(`Uploading ${pendingVideoFiles.current.length} video(s)...`);
          const formData = new FormData();
          pendingVideoFiles.current.forEach(f => formData.append('files', f));
          await axios.post(`${API}/jobcards/${jobCardId}/upload-videos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          uploadCount += pendingVideoFiles.current.length;
        } catch (err) {
          console.error('Video upload failed:', err);
          toast.error('Video upload failed - you can re-upload from Edit');
        }
      }

      if (uploadCount > 0) {
        toast.success(`Job card created with ${uploadCount} file(s) uploaded!`);
      } else {
        toast.success('Job card created successfully!');
      }
      onJobCardAdded();
      clearDraft();
      
      // Reset form and state
      reset({
        job_number: '',
        vehicle_id: '',
        customer_id: '',
        assigned_technician: '',
        priority: 'medium',
        customer_name: '',
        customer_location: '',
        customer_contact: '',
        customer_email: '',
        vehicle_make: '',
        vehicle_model: '',
        vehicle_series: '',
        year: '',
        color: '',
        vin: '',
        engine_model: '',
        transmission_model: '',
        fuel_type: '',
        vehicle_reg: '',
        mileage: '',
        remarks: '',
        scanning_report_filename: '',
        photo_filenames: '',
        video_filenames: '',
        video_files: null,
        vehicle_issues: [{ 
          id: 1, 
          title: '', 
          category: '', 
          dtc: '',
          manualTitle: false,
          manualDTC: false 
        }]
      });
      setSelectedVehicle(null);
      setSelectedCustomer(null);
      setUsedItems([]);
      setCustomerSearchTerm('');
      setVehicleSearchTerm('');
      setShowVehicleSuggestions(false);
      
      // Clear file inputs and refs
      pendingVideoFiles.current = [];
      pendingPhotoFiles.current = [];
      pendingPdfFile.current = null;
      if (window.photoFileInput) window.photoFileInput.value = '';
      if (window.videoFileInput) window.videoFileInput.value = '';
      
    } catch (error) {
      console.error('Error creating job card:', error);
      toast.error(error.response?.data?.detail || 'Failed to create job card');
    } finally {
      setIsSubmitting(false);
    }
  };

  

  

  

  

  

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {draftRestored && <DraftRestoredBar onDiscard={discardJobCardDraft} onDismiss={dismissBanner} />}
        {/* Basic Job Info */}
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <div>
            <Label htmlFor="job_number">Job Number</Label>
            <Input
              id="job_number"
              {...register("job_number")}
              placeholder="Leave blank for auto-generation"
            />
          </div>
        </div>

        {/* Customer Section */}
        <div className="border rounded-lg p-4 bg-blue-50">
          <h3 className="text-lg font-medium text-gray-900 mb-4">👤 Customer Information</h3>
          
          {/* Customer Search */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="customer_search">Search Existing Customer</Label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name, contact, or location..."
                  className="w-full h-10 px-3 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="jc-customer-search-input"
                  onInput={(e) => {
                    setCustomerSearchTerm(e.target.value);
                  }}
                />
                {customerSearchTerm && (
                  <button
                    type="button"
                    className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                    onClick={() => {
                      const el = document.querySelector('[data-testid="jc-customer-search-input"]');
                      if (el) el.value = '';
                      setCustomerSearchTerm('');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {customerSearchTerm && (
                <div className="text-xs text-gray-600 mt-1">
                  Found {filteredCustomers.length} customer(s) matching "{customerSearchTerm}"
                </div>
              )}
            </div>

            {/* Existing Customer Selection - Clickable List */}
            <div>
              <Label>Select Existing Customer (Optional)</Label>
              <div className="border rounded-md max-h-48 overflow-y-auto bg-white">
                {filteredCustomers.length > 0 ? (
                  filteredCustomers.map(customer => {
                    const customerVehicles = vehicles.filter(v => v && v.customer_id === customer.id);
                    const licensePlates = customerVehicles.map(v => v.license_plate || v.reg_number || '').filter(Boolean).join(', ');
                    const isSelected = watchedCustomerId === customer.id;
                    return (
                      <div
                        key={customer.id}
                        className={`px-3 py-2 cursor-pointer text-sm border-b last:border-b-0 transition-colors ${
                          isSelected 
                            ? 'bg-blue-50 text-blue-800 font-medium' 
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={() => handleCustomerSelection(customer.id)}
                        data-testid={`jc-customer-option-${customer.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{customer.name} {licensePlates ? `- ${licensePlates}` : ''} {customer.location ? `(${customer.location})` : ''}</span>
                          <div className="flex items-center gap-2">
                            {customerVehicles.length > 0 && (
                              <span className="text-xs text-gray-400">({customerVehicles.length} vehicle{customerVehicles.length !== 1 ? 's' : ''})</span>
                            )}
                            {isSelected && <span className="text-xs text-blue-600">Selected</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-3 text-sm text-gray-500 text-center">
                    {customerSearchTerm ? `No customers found matching "${customerSearchTerm}"` : 'No customers available'}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Customer Details Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <Label htmlFor="customer_name">Customer Name *</Label>
              <Input
                id="customer_name"
                {...register("customer_name", { required: "Customer name is required" })}
                placeholder="Enter customer name"
              />
              {errors.customer_name && <p className="text-red-500 text-sm mt-1">{errors.customer_name.message}</p>}
            </div>
            <div>
              <Label htmlFor="customer_contact">Contact Number *</Label>
              <Input
                id="customer_contact"
                {...register("customer_contact", { required: "Contact number is required" })}
                placeholder="+91 906173 9191"
              />
              {errors.customer_contact && <p className="text-red-500 text-sm mt-1">{errors.customer_contact.message}</p>}
            </div>
            <div>
              <Label htmlFor="customer_email">Email</Label>
              <Input
                id="customer_email"
                type="email"
                {...register("customer_email")}
                placeholder="customer@example.com"
              />
            </div>
              <Label htmlFor="customer_location">Location</Label>
              <Input
                id="customer_location"
                {...register("customer_location")}
                placeholder="Customer address/location"
              />
          </div>
        </div>

        {/* Vehicle Section */}
        <div className="border rounded-lg p-4 bg-green-50">
          <h3 className="text-lg font-medium text-gray-900 mb-4">🚗 Vehicle Information</h3>
          
          {/* Vehicle Search with Auto-Suggestions */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="vehicle_search">🔍 Search Vehicles (Type to filter)</Label>
              <div className="relative">
                <Input
                  placeholder={vehicles && vehicles.length > 0 
                    ? "Type to search by make, model, license plate, or VIN..." 
                    : "Loading vehicles..."}
                  value={vehicleSearchTerm}
                  onChange={(e) => {
                    setVehicleSearchTerm(e.target.value);
                    setShowVehicleSuggestions(true);
                  }}
                  onFocus={() => {
                    if (vehicleSearchTerm) setShowVehicleSuggestions(true);
                  }}
                  onBlur={() => {
                    // Delay hiding to allow clicking on suggestions
                    setTimeout(() => setShowVehicleSuggestions(false), 200);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setShowVehicleSuggestions(false);
                      setVehicleSearchTerm('');
                    }
                  }}
                  disabled={!vehicles || vehicles.length === 0}
                  className="flex-1"
                />
                
                {/* Auto-suggestion dropdown */}
                {showVehicleSuggestions && vehicleSearchTerm && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    
                    {filteredVehicles.length > 0 ? (
                      <>
                        {filteredVehicles.slice(0, 10).map(vehicle => (
                          <div
                            key={vehicle.id}
                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                            onMouseDown={(e) => {
                              e.preventDefault(); // Prevent blur from firing first
                              handleVehicleSelection(vehicle.id);
                              setVehicleSearchTerm('');
                              setShowVehicleSuggestions(false);
                            }}
                          >
                            <div className="flex justify-between items-center">
                              <div className="flex flex-col">
                                <span className="font-medium text-gray-900">
                                  {vehicle.year} {vehicle.make} {vehicle.model}
                                </span>
                                <div className="text-sm text-gray-600 mt-1">
                                  <span className="inline-flex items-center space-x-3">
                                    <span>📋 {vehicle.license_plate}</span>
                                    {vehicle.vin && <span>🔢 {vehicle.vin.substring(0, 8)}...</span>}
                                    {vehicle.color && <span>🎨 {vehicle.color}</span>}
                                  </span>
                                </div>
                                {vehicle.engine_no && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    Engine: {vehicle.engine_no} | Fuel: {vehicle.fuel_type || 'N/A'}
                                  </div>
                                )}
                              </div>
                              <div className="text-right text-sm text-gray-500">
                                {vehicle.mileage && <div>{vehicle.mileage} km</div>}
                                <div className="text-xs text-blue-600 mt-1">Click to select</div>
                              </div>
                            </div>
                          </div>
                        ))}
                        {filteredVehicles.length > 10 && (
                          <div className="px-4 py-2 text-sm text-gray-500 bg-gray-50 text-center">
                            Showing 10 of {filteredVehicles.length} vehicles. Keep typing to narrow results.
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="px-4 py-3 text-gray-500">
                        <div className="flex items-center justify-center space-x-2">
                          <span>🔍</span>
                          <span>No vehicles found matching "{vehicleSearchTerm}"</span>
                        </div>
                        <div className="text-xs mt-1 text-center">
                          Try searching by make, model, year, or license plate
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* No results message */}
                {showVehicleSuggestions && vehicleSearchTerm && filteredVehicles.length === 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                    <div className="px-4 py-3 text-gray-500 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <span>🔍</span>
                        <span>No vehicles found matching "{vehicleSearchTerm}"</span>
                      </div>
                      <div className="text-xs mt-1">Try searching by license plate, make, model, or VIN</div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Search results count and helpful tips */}
              <div className="text-xs text-gray-600 mt-1 space-y-1">
                {vehicles && vehicles.length > 0 ? (
                  vehicleSearchTerm ? (
                    filteredVehicles.length > 0 ? (
                      <>
                        <div>Found {filteredVehicles.length} vehicle(s) matching "{vehicleSearchTerm}"</div>
                        {filteredVehicles.length > 10 && (
                          <div className="text-blue-600">Showing top 10 results. Type more to narrow search.</div>
                        )}
                      </>
                    ) : (
                      <div className="text-amber-600">
                        No vehicles found for "{vehicleSearchTerm}". Try license plate, make, model, year, or VIN.
                      </div>
                    )
                  ) : (
                    <div className="text-gray-500">
                      {vehicles.length} vehicles available. Start typing to search.
                    </div>
                  )
                ) : (
                  <div className="text-gray-400">
                    Loading vehicles...
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Vehicle Details Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            {/* Make Dropdown */}
            <div>
              <Label htmlFor="vehicle_make">MAKE *</Label>
              <Controller
                name="vehicle_make"
                control={control}
                rules={{ required: "Vehicle make is required" }}
                render={({ field }) => (
                  <Select
                    value={field.value || ""}
                    onValueChange={(value) => {
                      field.onChange(value);
                      // Reset model when make changes
                      setValue('vehicle_model', '');
                    }}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Select vehicle MAKE" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {vehicleDataLoading ? (
                        <div className="p-2 text-sm text-gray-500 text-center">
                          Loading vehicle makes...
                        </div>
                      ) : availableMakes.length > 0 ? (
                        availableMakes
                          .filter(make => make && make.trim() !== '')
                          .map((make) => (
                            <SelectItem key={make} value={make}>
                              {make}
                            </SelectItem>
                          ))
                      ) : (
                        <div className="p-2 text-sm text-gray-500 text-center">
                          No vehicle makes available
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.vehicle_make && <p className="text-red-500 text-sm mt-1">{errors.vehicle_make.message}</p>}
            </div>

            {/* Model Dropdown - Dynamic based on Make */}
            <div>
              <Label htmlFor="vehicle_model">MODEL *</Label>
              <Controller
                name="vehicle_model"
                control={control}
                rules={{ required: "Vehicle model is required" }}
                render={({ field }) => (
                  <Select
                    key={watchedVehicleMake} // Force re-render when make changes
                    value={field.value || ""}
                    onValueChange={(value) => {
                      field.onChange(value);
                      // Reset series when model changes
                      setValue('vehicle_series', '');
                    }}
                    disabled={!watchedVehicleMake}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue 
                        placeholder={
                          watchedVehicleMake 
                            ? `Select ${watchedVehicleMake} MODEL` 
                            : "Select MAKE first"
                        } 
                      />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {availableModels.length > 0 ? (
                        availableModels.map((model) => (
                          <SelectItem key={model} value={model}>
                            {model}
                          </SelectItem>
                        ))
                      ) : watchedVehicleMake ? (
                        <div className="p-2 text-sm text-gray-500 text-center">
                          No predefined models for {watchedVehicleMake}
                        </div>
                      ) : null}
                      {watchedVehicleMake && watchedVehicleMake !== 'Other' && (
                        <SelectItem value="Other Model">Other {watchedVehicleMake} Model</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.vehicle_model && <p className="text-red-500 text-sm mt-1">{errors.vehicle_model.message}</p>}
              
              {/* Model Help Text */}
              {watchedVehicleMake && (
                <div className="text-xs text-gray-500 mt-1">
                  {availableModels.length > 0 
                    ? `${availableModels.length} ${watchedVehicleMake} MODELs available`
                    : `Select ${watchedVehicleMake} MODEL`
                  }
                </div>
              )}
            </div>

            {/* Series Dropdown - Dynamic based on Make and Model */}
            <div>
              <Label htmlFor="vehicle_series">SERIES</Label>
              <Controller
                name="vehicle_series"
                control={control}
                render={({ field }) => (
                  <Select
                    key={`${watchedVehicleMake}-${watchedVehicleModel}`} // Force re-render when make or model changes
                    value={field.value || ""}
                    onValueChange={field.onChange}
                    disabled={!watchedVehicleMake || !watchedVehicleModel}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue 
                        placeholder={
                          !watchedVehicleMake ? "Select MAKE first" :
                          !watchedVehicleModel ? "Select MODEL first" :
                          `Select ${watchedVehicleModel} SERIES`
                        } 
                      />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {availableSeries.length > 0 ? (
                        availableSeries.map((series) => (
                          <SelectItem key={series} value={series}>
                            {series}
                          </SelectItem>
                        ))
                      ) : watchedVehicleMake && watchedVehicleModel ? (
                        <div className="p-2 text-sm text-gray-500 text-center">
                          No predefined series for {watchedVehicleModel}
                        </div>
                      ) : null}
                      {watchedVehicleMake && watchedVehicleModel && watchedVehicleModel !== 'Other Model' && (
                        <SelectItem value="Other Series">Other {watchedVehicleModel} Series</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                )}
              />
              
              {/* Series Help Text */}
              {watchedVehicleMake && watchedVehicleModel && (
                <div className="text-xs text-gray-500 mt-1">
                  {availableSeries.length > 0 
                    ? `${availableSeries.length} ${watchedVehicleModel} SERIES available`
                    : `Select ${watchedVehicleModel} SERIES`
                  }
                </div>
              )}
              {!watchedVehicleMake || !watchedVehicleModel ? (
                <p className="text-xs text-gray-500 mt-1">Select MAKE and MODEL first to see SERIES options</p>
              ) : null}
            </div>

            <div>
              <Label htmlFor="year">Year *</Label>
              <Controller
                name="year"
                control={control}
                rules={{ required: "Year is required" }}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {Array.from({ length: 30 }, (_, i) => {
                        const year = new Date().getFullYear() - i;
                        return (
                          <SelectItem key={year} value={year.toString()}>
                            {year}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.year && <p className="text-red-500 text-sm mt-1">{errors.year.message}</p>}
            </div>

            <div>
              <Label htmlFor="vehicle_reg">License Plate *</Label>
              <Input
                id="vehicle_reg"
                {...register("vehicle_reg", { required: "License plate is required" })}
                placeholder="HR-26-AB-1234"
                className="uppercase"
              />
              {errors.vehicle_reg && <p className="text-red-500 text-sm mt-1">{errors.vehicle_reg.message}</p>}
            </div>

            <div>
              <Label htmlFor="vin">VIN</Label>
              <Input
                id="vin"
                {...register("vin")}
                placeholder="Vehicle Identification Number"
                className="uppercase"
                maxLength="17"
              />
            </div>

            <div>
              <Label htmlFor="mileage">Mileage (km)</Label>
              <Input
                id="mileage"
                type="number"
                {...register("mileage")}
                placeholder="Enter current mileage"
              />
            </div>

            <div>
              <Label htmlFor="color">Color</Label>
              <Controller
                name="color"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select color" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="White">⚪ White</SelectItem>
                      <SelectItem value="Black">⚫ Black</SelectItem>
                      <SelectItem value="Silver">🩶 Silver</SelectItem>
                      <SelectItem value="Red">🔴 Red</SelectItem>
                      <SelectItem value="Blue">🔵 Blue</SelectItem>
                      <SelectItem value="Green">🟢 Green</SelectItem>
                      <SelectItem value="Other">❓ Other</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div>
              <Label htmlFor="engine_model">Engine Model</Label>
              <Input
                id="engine_model"
                {...register("engine_model")}
                placeholder="Engine model/code"
              />
            </div>

            <div>
              <Label htmlFor="transmission_model">Transmission</Label>
              <Input
                id="transmission_model"
                {...register("transmission_model")}
                placeholder="Transmission type"
              />
            </div>

            <div>
              <Label htmlFor="fuel_type">Fuel Type</Label>
              <Controller
                name="fuel_type"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select fuel type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Petrol">⛽ Petrol</SelectItem>
                      <SelectItem value="Diesel">🛢️ Diesel</SelectItem>
                      <SelectItem value="CNG">🌿 CNG</SelectItem>
                      <SelectItem value="Electric">⚡ Electric</SelectItem>
                      <SelectItem value="Hybrid">🔋 Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>
        </div>

        {/* Additional Job Details */}
        <div className="border rounded-lg p-4 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900 mb-4">📋 Job Details</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="assigned_technician">Assigned Technician</Label>
              <Controller
                name="assigned_technician"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select technician" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees
                        .filter(emp => 
                          emp.position === 'technician' || 
                          emp.position === 'senior_technician' || 
                          emp.position === 'junior_technician' ||
                          emp.position === 'senior_mechanic' ||
                          emp.position === 'junior_mechanic'
                        )
                        .map((employee) => (
                          <SelectItem key={employee.id} value={employee.name}>
                            {employee.name} - {employee.position.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </SelectItem>
                        ))
                      }
                      {employees.filter(emp => 
                        emp.position === 'technician' || 
                        emp.position === 'senior_technician' || 
                        emp.position === 'junior_technician' ||
                        emp.position === 'senior_mechanic' ||
                        emp.position === 'junior_mechanic'
                      ).length === 0 && (
                        <SelectItem value="no-technicians" disabled>
                          No technicians available
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            
            <div>
              <Label htmlFor="priority">Priority</Label>
              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">🟢 Low</SelectItem>
                      <SelectItem value="medium">🟡 Medium</SelectItem>
                      <SelectItem value="high">🟠 High</SelectItem>
                      <SelectItem value="critical">🔴 Critical</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>
          
          <div className="mt-4">
            <Label htmlFor="remarks">Additional Remarks</Label>
            <Textarea
              id="remarks"
              {...register("remarks")}
              placeholder="Enter any additional notes or remarks about the job..."
              rows={3}
            />
          </div>
        </div>

        <JobCardUsedItems
          items={usedItems}
          onItemsChange={setUsedItems}
          vehicleMake={watchedVehicleMake}
          vehicleModel={watchedVehicleModel}
        />

        {/* Vehicle Problems - Dynamic Add/Remove System */}
        <div className="border border-gray-300 rounded-lg p-6 bg-white shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <span className="mr-3 text-lg">🔧</span>
              Vehicle Issues - Title and Category
            </h3>
            <Button
              type="button"
              variant="default"
              size="default"
              onClick={() => appendIssue({ id: Date.now(), title: '', category: '', subcategory: '', manualTitle: false })}
              className="bg-gray-600 hover:bg-gray-700 text-white font-medium px-4 py-2 rounded-md shadow-sm transition-colors duration-200"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Issue
            </Button>
          </div>
          
          {/* Dynamic Issues List */}
          <div className="space-y-4">
            {issueFields.map((issue, index) => (
              <div 
                key={issue.id} 
                className="grid grid-cols-1 md:grid-cols-12 gap-6 p-4 border border-gray-200 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
              >
                
                {/* Category Column - Professional Design */}
                <div className="md:col-span-3">
                  <div className="space-y-3">
                    {/* Header */}
                    <div className="h-6 flex items-center">
                      <Label className="text-sm font-medium text-gray-700">Category</Label>
                    </div>
                    
                    {/* Native select (avoids Radix Select event-swallowing inside Dialog) */}
                    <select
                      value={issue.category || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        const idx = issueFields.findIndex(f => f.id === issue.id);
                        if (idx >= 0) {
                          updateIssueAt(idx, {
                            ...issueFields[idx],
                            category: value,
                            subcategory: '',
                            title: issue.manualTitle ? issueFields[idx].title : ''
                          });
                        }
                      }}
                      className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                      aria-label={`Category for issue ${issue.id}`}
                      data-testid={`jobcard-issue-category-${issue.id}`}
                    >
                      <option value="">
                        {getDbCategories().length === 0 
                          ? 'No categories in DB' 
                          : `Select Category (${getDbCategories().length} available)`}
                      </option>
                      {getDbCategories().map(cat => (
                        <option key={cat} value={cat}>{capitalizeWords(cat)}</option>
                      ))}
                    </select>
                    {issue.category && (
                      <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded border">
                        ✓ {capitalizeWords(issue.category)} selected
                      </div>
                    )}
                    {getDbCategories().length === 0 && (
                      <div className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                        💡 Add problems to the Enhanced Vehicle Problems Database first.
                      </div>
                    )}
                  </div>
                </div>

                {/* Sub-Category Column - Professional Design */}
                <div className="md:col-span-3">
                  <div className="space-y-3">
                    {/* Header */}
                    <div className="h-6 flex items-center">
                      <Label className="text-sm font-medium text-gray-700">Sub-Category</Label>
                    </div>
                    
                    {/* Native subcategory select (avoids Radix swallow) */}
                    <select
                      value={issue.subcategory || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        console.log('Subcategory selected:', value, 'for issue:', issue.id);
                        updateVehicleIssue(issue.id, 'subcategory', value);
                      }}
                      disabled={!issue.category}
                      className={`h-10 w-full rounded-md border px-3 text-sm focus:outline-none ${
                        !issue.category 
                          ? 'border-gray-200 bg-gray-100 cursor-not-allowed text-gray-400' 
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
                      }`}
                      aria-label={`Subcategory for issue ${issue.id}`}
                      data-testid={`jobcard-issue-subcategory-${issue.id}`}
                    >
                      <option value="">
                        {issue.category 
                          ? (getDbSubcategories(issue.category).length === 0 
                              ? `No subcategories for ${capitalizeWords(issue.category)}` 
                              : `Select ${capitalizeWords(issue.category)} subcategory (${getDbSubcategories(issue.category).length})`) 
                          : 'Select category first'}
                      </option>
                      {getDbSubcategories(issue.category).map((subcategory) => (
                        <option key={subcategory} value={subcategory}>{capitalizeWords(subcategory)}</option>
                      ))}
                    </select>
                    {issue.category && getDbSubcategories(issue.category).length > 0 && (
                      <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded border">
                        {getDbSubcategories(issue.category).length} options
                        {issue.subcategory && ` • ✓ ${capitalizeWords(issue.subcategory)}`}
                      </div>
                    )}
                  </div>
                </div>

                {/* Issue Title Column - Professional Design */}
                <div className="md:col-span-6">
                  <div className="space-y-3">
                    {/* Toggle Header */}
                    <div className="flex items-center justify-between h-6">
                      <Label className="text-sm font-medium text-gray-700">
                        {issue.manualTitle ? 'Manual Entry' : 'Select from List'}
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => updateVehicleIssue(issue.id, 'manualTitle', !issue.manualTitle)}
                        className="text-xs h-6 px-2 text-gray-600 hover:bg-gray-100 border-gray-300 rounded transition-colors duration-200"
                      >
                        {issue.manualTitle ? 'Use List' : 'Manual'}
                      </Button>
                    </div>
                    
                    {/* Input/Dropdown */}
                    {issue.manualTitle ? (
                      <Input
                        key={`title-input-${issue.id}`}
                        placeholder="Enter custom issue title - describe the problem in detail"
                        {...register(`vehicle_issues.${index}.title`)}
                        className="w-full h-10 border border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      />
                    ) : (() => {
                        const currentIssue = issueFields.find(i => i.id === issue.id) || issue;
                        const opts = getFilteredIssueTitles(currentIssue.category, currentIssue.subcategory);
                        return (
                          <>
                            <select
                              value={issue.title || ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                const idx = issueFields.findIndex(f => f.id === issue.id);
                                if (idx >= 0) {
                                  updateIssueAt(idx, { ...issueFields[idx], title: value });
                                }
                              }}
                              disabled={!currentIssue.category}
                              className={`h-10 w-full rounded-md border px-3 text-sm focus:outline-none ${
                                !currentIssue.category 
                                  ? 'border-gray-200 bg-gray-100 cursor-not-allowed text-gray-400'
                                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
                              }`}
                              data-testid={`jobcard-issue-title-${issue.id}`}
                            >
                              <option value="">
                                {!currentIssue.category 
                                  ? 'Select category first'
                                  : opts.length === 0 
                                    ? `No problems in DB for ${capitalizeWords(currentIssue.category)}${currentIssue.subcategory ? ' → ' + capitalizeWords(currentIssue.subcategory) : ''}` 
                                    : `Select ${capitalizeWords(currentIssue.category)} issue (${opts.length} available)`}
                              </option>
                              {opts.map((issueOption) => (
                                <option key={issueOption.value} value={issueOption.value}>{issueOption.label}</option>
                              ))}
                            </select>
                            {currentIssue.category && opts.length === 0 && (
                              <div className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                                💡 No matching problems in Enhanced Vehicle Problems Database. Use <strong>Manual</strong> mode to type your own title.
                              </div>
                            )}
                          </>
                        );
                      })()}
                    {issue.title && (
                      <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded border">
                        ✓ {issue.title.length > 50 ? `${issue.title.substring(0, 50)}...` : issue.title}
                      </div>
                    )}
                  </div>
                </div>
                
                
                {/* Remove Issue Button - Professional Design */}
                <div className="md:col-span-12 flex justify-end items-center pt-4 border-t border-indigo-100">
                  {issueFields.length > 1 && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => removeVehicleIssue(issue.id)}
                      className="bg-red-500 hover:bg-red-600 text-white font-medium px-4 py-2 shadow-md hover:shadow-lg transition-all duration-200"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Remove Issue
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Issue Count Display */}
          <div className="text-sm text-gray-600 mt-4 text-center bg-gray-50 py-2 rounded-lg border">
            <span className="font-medium">
              {issueFields.length === 1 ? '1 issue added' : `${issueFields.length} issues added`}
            </span>
          </div>
        </div>

        {/* Standalone DTC Codes Section */}
        <div className="border border-gray-300 rounded-lg p-6 bg-white shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <span className="mr-3 text-lg">🔍</span>
              Diagnostic Trouble Codes (DTC)
            </h3>
            <Button
              type="button"
              variant="default"
              size="default"
              onClick={addDtcCode}
              className="bg-gray-600 hover:bg-gray-700 text-white font-medium px-4 py-2 rounded-md shadow-sm transition-colors duration-200"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add DTC Code
            </Button>
          </div>
          
          {/* DTC Codes List */}
          <div className="space-y-4">
            {dtcFields.map((dtc, index) => (
              <div 
                key={`dtc-${dtc.id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 border border-gray-200 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
              >
                {/* DTC Code Column */}
                <div className="md:col-span-4">
                  <div className="space-y-3">
                    {/* Toggle Header */}
                    <div className="flex items-center justify-between h-6">
                      <Label className="text-sm font-medium text-gray-700">
                        {dtc.manual ? 'Manual Entry' : 'Select DTC'}
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => updateDtcCode(dtc.id, 'manual', !dtc.manual)}
                        className="text-xs h-6 px-2 text-gray-600 hover:bg-gray-100 border-gray-300 rounded transition-colors duration-200"
                      >
                        {dtc.manual ? 'List' : 'Manual'}
                      </Button>
                    </div>
                    
                    {/* DTC Input/Dropdown */}
                    {dtc.manual ? (
                      <Input
                        placeholder="Enter DTC (e.g., P0420)"
                        {...register(`dtc_codes.${index}.code`)}
                        className="h-10 border border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 font-mono text-center"
                        maxLength="10"
                        style={{ textTransform: 'uppercase' }}
                      />
                    ) : (
                      <Select
                        value={dtc.code || ""}
                        onValueChange={(value) => updateDtcCode(dtc.id, 'code', value)}
                      >
                        <SelectTrigger className="h-10 border border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
                          <SelectValue placeholder="Select DTC Code" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60 z-50 bg-white border border-gray-200 rounded-md shadow-lg">
                          {/* Engine DTC Codes */}
                          <SelectItem value="P0300">P0300 - Random/Multiple Cylinder Misfire</SelectItem>
                          <SelectItem value="P0301">P0301 - Cylinder 1 Misfire Detected</SelectItem>
                          <SelectItem value="P0302">P0302 - Cylinder 2 Misfire Detected</SelectItem>
                          <SelectItem value="P0420">P0420 - Catalyst System Efficiency Below Threshold</SelectItem>
                          <SelectItem value="P0171">P0171 - System Too Lean (Bank 1)</SelectItem>
                          <SelectItem value="P0172">P0172 - System Too Rich (Bank 1)</SelectItem>
                          
                          {/* Transmission DTC Codes */}
                          <SelectItem value="P0700">P0700 - Transmission Control System Malfunction</SelectItem>
                          <SelectItem value="P0715">P0715 - Input/Turbine Speed Sensor Circuit</SelectItem>
                          
                          {/* Electrical DTC Codes */}
                          <SelectItem value="B0001">B0001 - Driver Airbag Circuit</SelectItem>
                          <SelectItem value="U0100">U0100 - Lost Communication with ECM/PCM</SelectItem>
                          
                          <SelectItem value="Custom">🔧 Custom DTC Code</SelectItem>
                          <SelectItem value="No DTC">❌ No DTC Code Found</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>

                {/* Description Column */}
                <div className="md:col-span-6">
                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">Description / Notes</Label>
                    <Textarea
                      placeholder="Enter detailed description of the diagnostic trouble code, symptoms, and diagnostic notes..."
                      {...register(`dtc_codes.${index}.description`)}
                      className="h-20 border border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors duration-200"
                      rows={3}
                    />
                  </div>
                </div>

                {/* Actions Column */}
                <div className="md:col-span-2 flex flex-col justify-center items-center space-y-2">
                  {dtc.code && (
                    <div className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded font-mono font-medium text-center w-full">
                      ✓ {dtc.code}
                    </div>
                  )}
                  {dtcFields.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDtcCode(dtc.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0 rounded-full border border-red-200"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* DTC Count Display */}
          <div className="text-sm text-gray-600 mt-4 text-center bg-gray-50 py-2 rounded-md border border-gray-200">
            <span className="font-medium">
              {dtcFields.length === 1 
                ? '1 DTC code added' 
                : `${dtcFields.length} DTC codes added`
              }
            </span>
          </div>
        </div>

        {/* Scanning Report Section - Restored */}
        <div className="border rounded-lg p-4 bg-red-50">
          <h3 className="text-lg font-medium text-gray-900 mb-4">📊 Diagnostic Scanning Report</h3>
          
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label>Upload Scanning Report PDF</Label>
              <div className="flex space-x-2">
                <Input
                  {...register("scanning_report_filename")}
                  placeholder="diagnostic_report_2024.pdf"
                  className="flex-1"
                />
                <input
                  type="file"
                  accept=".pdf"
                  multiple
                  style={{ display: 'none' }}
                  ref={(el) => window.pdfFileInput = el}
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length > 0) {
                      pendingPdfFile.current = files;
                      setValue('scanning_report_filename', files.map(f => f.name).join(', '));
                      toast.success(`${files.length} PDF(s) selected`);
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
                  onClick={() => window.pdfFileInput?.click()}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add PDF(s)
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Vehicle Photos Section - Restored */}
        <div className="border rounded-lg p-4 bg-cyan-50">
          <h3 className="text-lg font-medium text-gray-900 mb-4">📷 Vehicle Photos</h3>
          
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label>Upload Vehicle Photos</Label>
              <div className="flex space-x-2">
                <Input
                  {...register("photo_filenames")}
                  placeholder="photo1.jpg, photo2.jpg, photo3.jpg"
                  className="flex-1"
                />
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  ref={(el) => window.photoFileInput = el}
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    if (files.length > 0) {
                      pendingPhotoFiles.current = files;
                      const fileNames = files.map(file => file.name).join(', ');
                      setValue('photo_filenames', fileNames);
                      toast.success(`${files.length} photo(s) selected`);
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                  onClick={() => window.photoFileInput?.click()}
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Add Photos
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Vehicle Videos Section - NEW */}
        <div className="border rounded-lg p-4 bg-purple-50">
          <h3 className="text-lg font-medium text-gray-900 mb-4">🎥 Vehicle Videos (Max 10)</h3>
          
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label>Upload Vehicle Videos</Label>
              <div className="flex space-x-2">
                <Input
                  {...register("video_filenames")}
                  placeholder="video1.mp4, video2.mov (Max 10 videos)"
                  className="flex-1"
                  readOnly
                />
                <input
                  type="file"
                  accept="video/mp4,video/mov,video/avi,video/wmv,video/mkv,video/webm"
                  multiple
                  style={{ display: 'none' }}
                  ref={(el) => window.videoFileInput = el}
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    if (files.length > 10) {
                      toast.error('Maximum 10 videos allowed. Please select fewer files.');
                      e.target.value = ''; // Clear the selection
                      return;
                    }
                    
                    // Check file sizes (limit to 100MB per video)
                    const oversizedFiles = files.filter(file => file.size > 100 * 1024 * 1024);
                    if (oversizedFiles.length > 0) {
                      toast.error(`Some videos are too large (max 100MB per video): ${oversizedFiles.map(f => f.name).join(', ')}`);
                      return;
                    }
                    
                    if (files.length > 0) {
                      pendingVideoFiles.current = files;
                      const fileNames = files.map(file => file.name).join(', ');
                      setValue('video_filenames', fileNames);
                      toast.success(`${files.length} video(s) selected (${files.length}/10)`);
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-300"
                  onClick={() => window.videoFileInput?.click()}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Videos
                </Button>
              </div>
              
              {/* Video Info */}
              <div className="mt-2 text-sm text-gray-600">
                <p>• Supported formats: MP4, MOV, AVI, WMV, MKV, WebM</p>
                <p>• Maximum 10 videos per job card</p>
                <p>• Maximum file size: 100MB per video</p>
              </div>
              
              {/* Video Preview/List */}
              {watch("video_filenames") && (
                <div className="mt-3 p-3 bg-purple-25 rounded border">
                  <p className="text-sm font-medium text-purple-800 mb-2">Selected Videos:</p>
                  <div className="space-y-1">
                    {watch("video_filenames").split(', ').filter(name => name.trim()).map((filename, index) => (
                      <div key={index} className="flex items-center text-sm text-purple-700">
                        <span className="mr-2">🎥</span>
                        <span className="truncate">{filename}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex space-x-4 pt-4">
          <Button 
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            {isSubmitting ? 'Creating Job Card...' : 'Create Job Card'}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Parts Request — request parts against a job card; sends them to a draft Purchase Order
const PartsRequestManager = ({ jobCard }) => {
  const [rows, setRows] = useState([{ part_name: '', oe_number: '', quantity: 1 }]);
  const [sending, setSending] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [requests, setRequests] = useState([]);
  const [cancelling, setCancelling] = useState(null);

  const loadRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/jobcards/${jobCard.id}/parts-request`);
      setRequests(res.data?.requests || []);
    } catch (error) {
      console.error('Failed to load requested parts:', error);
    }
  }, [jobCard.id]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/parts`);
        const list = Array.isArray(res.data) ? res.data : (res.data?.parts || []);
        if (active) setInventory(list);
      } catch (error) {
        console.error('Failed to load parts inventory:', error);
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const cancelRequestedPart = async (poId, itemIndex) => {
    setCancelling(`${poId}-${itemIndex}`);
    try {
      const res = await axios.delete(`${API}/purchase-orders/${poId}/items/${itemIndex}`);
      toast.success(res.data?.po_deleted ? 'Part cancelled — empty Purchase Order removed' : 'Part removed from Purchase Order');
      await loadRequests();
    } catch (error) {
      console.error('Cancel failed:', error);
      toast.error(error?.response?.data?.detail || 'Failed to cancel part');
    } finally {
      setCancelling(null);
    }
  };

  const updateRow = (index, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };
  const addRow = () => setRows((prev) => [...prev, { part_name: '', oe_number: '', quantity: 1 }]);
  const removeRow = (index) => setRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));

  const selectPart = (index, part) => {
    setRows((prev) => prev.map((r, i) => (
      i === index ? { ...r, part_name: part.part_name || '', oe_number: part.part_number || '' } : r
    )));
    setActiveIndex(null);
  };

  const getSuggestions = (query) => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return [];
    return inventory
      .filter((p) => (p.part_name || '').toLowerCase().includes(q) || (p.part_number || '').toLowerCase().includes(q))
      .slice(0, 8);
  };

  const vehicleRef = [jobCard.vehicle_make, jobCard.vehicle_model, jobCard.vehicle_series, jobCard.vehicle_year, jobCard.vehicle_reg]
    .filter(Boolean)
    .join(' ');

  const sendRequest = async () => {
    const items = rows
      .filter((r) => (r.part_name || '').trim())
      .map((r) => ({ part_name: r.part_name.trim(), oe_number: (r.oe_number || '').trim(), quantity: Number(r.quantity) || 1 }));
    if (items.length === 0) {
      toast.error('Add at least one part name before sending the request');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/jobcards/${jobCard.id}/parts-request`, { items });
      toast.success(`Parts request sent to Purchase Orders (${res.data.po_number})`);
      setRows([{ part_name: '', oe_number: '', quantity: 1 }]);
      await loadRequests();
    } catch (error) {
      console.error('Parts request failed:', error);
      toast.error(error?.response?.data?.detail || 'Failed to send parts request');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="border-amber-200" data-testid="parts-request-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-amber-700">
          <Package className="h-5 w-5" /> Parts Request
        </CardTitle>
        <CardDescription>
          Request parts for Job Card <strong>#{jobCard.job_number || jobCard.id}</strong>
          {vehicleRef ? <> · Vehicle: <strong>{vehicleRef}</strong></> : null}. Start typing a part name to pick from inventory (OE no. auto-fills). Sending creates a draft Purchase Order (assign supplier later).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {requests.length > 0 && (
          <div className="space-y-3 mb-2" data-testid="parts-requested-list">
            <p className="text-sm font-semibold text-gray-700">Already Requested</p>
            {requests.map((req) => (
              <div key={req.po_id} className="border rounded-md p-3 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm">
                    <span className="font-medium">{req.po_number}</span>
                    <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-xs capitalize bg-amber-100 text-amber-700">{req.status}</span>
                    {req.supplier_name ? <span className="ml-2 text-gray-500">· {req.supplier_name}</span> : <span className="ml-2 text-gray-400">· No supplier</span>}
                  </div>
                </div>
                <div className="space-y-1">
                  {req.items.map((it) => (
                    <div key={it.index} className="flex items-center justify-between text-sm bg-white border rounded px-2 py-1" data-testid={`requested-part-${req.po_id}-${it.index}`}>
                      <span>
                        <span className="font-medium">{it.part_name}</span>
                        {it.part_number ? <span className="text-gray-500"> · OE: {it.part_number}</span> : null}
                        <span className="text-gray-500"> · Qty: {it.quantity}</span>
                      </span>
                      {req.can_cancel ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                          disabled={cancelling === `${req.po_id}-${it.index}`}
                          onClick={() => cancelRequestedPart(req.po_id, it.index)}
                          data-testid={`cancel-requested-part-${req.po_id}-${it.index}`}
                        >
                          {cancelling === `${req.po_id}-${it.index}`
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <><X className="h-3.5 w-3.5 mr-1" /> Cancel</>}
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-400">Ordered</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="hidden md:grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 px-1">
          <div className="col-span-6">Part Name</div>
          <div className="col-span-3">OE No.</div>
          <div className="col-span-2">Qty</div>
          <div className="col-span-1"></div>
        </div>
        {rows.map((row, index) => {
          const suggestions = activeIndex === index ? getSuggestions(row.part_name) : [];
          return (
            <div key={index} className="grid grid-cols-12 gap-2 items-center" data-testid={`parts-request-row-${index}`}>
              <div className="col-span-12 md:col-span-6 relative">
                <Input
                  placeholder="Part name"
                  value={row.part_name}
                  autoComplete="off"
                  onChange={(e) => { updateRow(index, 'part_name', e.target.value); setActiveIndex(index); }}
                  onFocus={() => setActiveIndex(index)}
                  onBlur={() => setTimeout(() => setActiveIndex((cur) => (cur === index ? null : cur)), 150)}
                  data-testid={`parts-request-name-${index}`}
                />
                {suggestions.length > 0 && (
                  <div
                    className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-auto"
                    data-testid={`parts-request-suggestions-${index}`}
                  >
                    {suggestions.map((p, si) => (
                      <button
                        key={p.id || si}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm border-b last:border-b-0"
                        onMouseDown={(e) => { e.preventDefault(); selectPart(index, p); }}
                        data-testid={`parts-request-suggestion-${index}-${si}`}
                      >
                        <span className="font-medium">{p.part_name}</span>
                        {p.part_number ? <span className="text-gray-500"> · OE: {p.part_number}</span> : null}
                        {typeof p.stock_quantity !== 'undefined' ? (
                          <span className="text-gray-400 float-right">Stock: {p.stock_quantity}</span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Input
                className="col-span-7 md:col-span-3"
                placeholder="OE number"
                value={row.oe_number}
                onChange={(e) => updateRow(index, 'oe_number', e.target.value)}
                data-testid={`parts-request-oe-${index}`}
              />
              <Input
                type="number"
                min="1"
                className="col-span-3 md:col-span-2"
                value={row.quantity}
                onChange={(e) => updateRow(index, 'quantity', e.target.value)}
                data-testid={`parts-request-qty-${index}`}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="col-span-2 md:col-span-1 text-red-500 hover:text-red-700"
                onClick={() => removeRow(index)}
                data-testid={`parts-request-remove-${index}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
        <div className="flex flex-col sm:flex-row gap-2 pt-1">
          <Button type="button" variant="outline" onClick={addRow} data-testid="parts-request-add-row">
            <Plus className="h-4 w-4 mr-1" /> Add Part
          </Button>
          <Button
            type="button"
            onClick={sendRequest}
            disabled={sending}
            className="bg-amber-600 hover:bg-amber-700 sm:ml-auto"
            data-testid="parts-request-send"
          >
            {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            Send Parts Request
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};



// Enhanced Edit Job Card Form Component with Vehicle Issues Management

const EditJobCardForm = ({ jobCard, onJobCardUpdated, onJobCardRefresh, customers, vehicles, employees }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [usedItems, setUsedItems] = useState(jobCard.parts_used || []);

  // Enhanced Vehicle Problems fetched from /api/vehicle-issues
  const [enhancedProblems, setEnhancedProblems] = useState([]);
  useEffect(() => {
    const fetchEnhancedProblems = async () => {
      try {
        const response = await axios.get(`${API}/vehicle-issues`);
        setEnhancedProblems(response.data || []);
      } catch (error) {
        console.error('Failed to fetch enhanced vehicle problems:', error);
        setEnhancedProblems([]);
      }
    };
    fetchEnhancedProblems();
  }, []);
  
  // Initialize vehicle issues from the job card
  const initializeVehicleIssues = () => {
    try {
      if (jobCard.vehicle_issues && Array.isArray(jobCard.vehicle_issues) && jobCard.vehicle_issues.length > 0) {
        return jobCard.vehicle_issues.map((issue, index) => ({
          id: issue.id || index + 1,
          title: String(issue.title || ''),
          category: String(issue.category || ''),
          dtc: String(issue.dtc || ''),
          manualTitle: true, // Default to manual mode for existing issues
          manualDTC: true    // Default to manual mode for existing DTCs
        }));
      }
    } catch (error) {
      console.warn('Error initializing vehicle issues:', error);
    }
    
    return [{ 
      id: 1, 
      title: '', 
      category: '', 
      dtc: '', 
      manualTitle: false, // Start with dropdown mode for new issues
      manualDTC: false    // Start with dropdown mode for new DTCs
    }];
  };

  // React Hook Form setup
  const { 
    register, 
    handleSubmit, 
    formState: { errors }, 
    setValue, 
    watch, 
    control
  } = useForm({
    defaultValues: {
      title: String(jobCard.title || ''),
      description: String(jobCard.description || ''),
      status: String(jobCard.status || 'created'),
      assigned_technician: String(jobCard.assigned_technician || 'unassigned'),
      priority: String(jobCard.priority || 'medium'),
      estimated_hours: String(jobCard.estimated_hours || ''),
      actual_hours: String(jobCard.actual_hours || ''),
      progress_percentage: String(jobCard.progress_percentage || '0'),
      notes: String(jobCard.notes || ''),
      scanning_report_filename: String(jobCard.scanning_report_filename || ''),
      photo_filenames: String(jobCard.photo_filenames || ''),
      video_filenames: String(jobCard.video_filenames || ''),
      rework_reason: '',
      vehicle_issues: initializeVehicleIssues()
    }
  });

  // Keep status/assigned_technician in sync with the live job card — these two
  // fields can change from OUTSIDE this form (WorkHoldManager/TimeTracker call
  // onJobCardRefresh behind the scenes) and react-hook-form's defaultValues are
  // only applied once on mount, so without this a later "Update Job Card" save
  // would silently revert a hold/resume/clock-in/out that happened meanwhile.
  useEffect(() => {
    setValue('status', String(jobCard.status || 'created'));
    setValue('assigned_technician', String(jobCard.assigned_technician || 'unassigned'));
  }, [jobCard.status, jobCard.assigned_technician, jobCard.is_on_hold, setValue]);

  // Watch Vehicle Issues from React Hook Form - Use local state to prevent re-render on keystroke
  // useFieldArray for Vehicle Issues in Edit form
  const { fields: editIssueFields, append: appendEditIssue, remove: removeEditIssueAt, update: updateEditIssueAt } = useFieldArray({
    control,
    name: 'vehicle_issues',
    keyName: '_rhfKey'
  });

  // Wrapper functions for Edit form
  const addVehicleIssue = () => {
    appendEditIssue({ id: Date.now(), title: '', category: '', subcategory: '', dtc: '', manualTitle: false, manualDTC: false });
  };

  const removeVehicleIssue = (issueId) => {
    const idx = editIssueFields.findIndex(f => f.id === issueId);
    if (idx >= 0 && editIssueFields.length > 1) removeEditIssueAt(idx);
  };

  const updateVehicleIssue = (issueId, field, value) => {
    const idx = editIssueFields.findIndex(f => f.id === issueId);
    if (idx >= 0) {
      const current = editIssueFields[idx];
      updateEditIssueAt(idx, { ...current, [field]: value });
    }
  };

  // Get filtered issue titles based on category
  const getFilteredIssueTitles = (category, subcategory) => {
    const dbOptions = enhancedProblems
      .filter(p => {
        if (!p.title) return false;
        if (category && p.category !== category) return false;
        if (subcategory && p.subcategory && p.subcategory !== subcategory) return false;
        return true;
      })
      .map(p => ({
        value: p.title,
        label: p.is_solved ? `${p.title} ✓` : p.title,
        category: p.category || 'Other',
        subcategory: p.subcategory || '',
        source: 'enhanced_db'
      }));
    const seen = new Set();
    return dbOptions.filter(o => {
      const key = o.value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // Helpers to derive Category & Subcategory lists from the Enhanced Vehicle Problems DB
  const getDbCategories = () => {
    const cats = new Set();
    enhancedProblems.forEach(p => {
      if (p.category) cats.add(p.category);
    });
    return Array.from(cats).sort();
  };

  const getDbSubcategories = (category) => {
    if (!category) return [];
    const subs = new Set();
    enhancedProblems.forEach(p => {
      if (p.category === category && p.subcategory) subs.add(p.subcategory);
    });
    return Array.from(subs).sort();
  };

  const onSubmit = async (data) => {
    // Guard: warn if a timer is still running when marking the job complete
    const hasActiveTimer = (jobCard.active_sessions && jobCard.active_sessions.length > 0) || jobCard.current_session;
    if ((data.status === 'completed' || data.status === 'delivered') && hasActiveTimer) {
      const proceed = window.confirm('A work timer is still running on this job card. Marking it complete will NOT stop the timer. Stop the timer first, or continue anyway?');
      if (!proceed) return;
    }
    setIsSubmitting(true);

    try {
      const updateData = {
        ...data,
        estimated_hours: parseFloat(data.estimated_hours) || null,
        actual_hours: parseFloat(data.actual_hours) || null,
        progress_percentage: parseInt(data.progress_percentage) || 0,
        vehicle_issues: data.vehicle_issues,
        parts_used: usedItems,
        scanning_report_filename: data.scanning_report_filename || '',
        photo_filenames: data.photo_filenames || '',
        video_filenames: data.video_filenames || ''
      };

      await axios.put(`${API}/jobcards/${jobCard.id}`, updateData);
      toast.success('Job card updated successfully!');
      onJobCardUpdated();
    } catch (error) {
      console.error('Error updating job card:', error);
      toast.error(error.response?.data?.detail || 'Failed to update job card');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-sm text-gray-600 mb-4">
        Update job card details and progress - #{jobCard.job_number}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="title">Job Title *</Label>
            <Input
              id="title"
              {...register('title', { required: 'Job title is required' })}
              placeholder="Enter job title"
            />
            {errors.title && <p className="text-red-500 text-sm mt-1">{String(errors.title?.message || 'Invalid input')}</p>}
          </div>
          <div>
            <Label htmlFor="status">Status</Label>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <JobCardStatusWorkflow 
                  jobCard={{...jobCard, status: field.value}}
                  onStatusChange={field.onChange}
                  onReworkReasonChange={(reason) => setValue('rework_reason', reason)}
                  isAdmin={isAdmin}
                />
              )}
            />
            <input type="hidden" {...register('rework_reason')} />
          </div>
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            {...register('description')}
            rows="3"
            placeholder="Job description"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="assigned_technician">Assigned Technician</Label>
            <Controller
              name="assigned_technician"
              control={control}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger>
                    <SelectValue placeholder="Assign technician" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {employees
                      .filter(employee => 
                        employee.status === 'active' && 
                        (employee.department === 'service' || 
                         employee.department === 'technical' || 
                         employee.role === 'technician' || 
                         employee.position?.toLowerCase().includes('technician') ||
                         employee.position?.toLowerCase().includes('mechanic') ||
                         employee.position?.toLowerCase().includes('advisor'))
                      )
                      .map(employee => (
                        <SelectItem key={employee.id} value={employee.name}>
                          {employee.name} - {employee.position || employee.role}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div>
            <Label htmlFor="priority">Priority</Label>
            <Controller
              name="priority"
              control={control}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="estimated_hours">Estimated Hours</Label>
            <Input
              id="estimated_hours"
              type="number"
              {...register('estimated_hours')}
              min="0"
              step="0.5"
              placeholder="0.0"
            />
          </div>
          <div>
            <Label htmlFor="actual_hours">Actual Hours</Label>
            <Input
              id="actual_hours"
              type="number"
              {...register('actual_hours')}
              min="0"
              step="0.5"
              placeholder="0.0"
            />
          </div>
          <div>
            <Label htmlFor="progress_percentage">Progress %</Label>
            <Input
              id="progress_percentage"
              type="number"
              {...register('progress_percentage')}
              min="0"
              max="100"
              placeholder="0"
            />
          </div>
        </div>

        {/* Vehicle Issues Management - Advanced */}
        <div className="border rounded-lg p-6 bg-gradient-to-r from-orange-50 to-amber-50">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <span className="mr-2">🔧</span>
              Vehicle Issues - Title and Category
            </h3>
            <Button
              type="button"
              variant="outline"
              size="default"
              onClick={addVehicleIssue}
              className="text-orange-600 border-orange-300 hover:bg-orange-100 font-medium px-4 py-2"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Issue
            </Button>
          </div>
          
          {/* Column Headers */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 bg-gray-50 rounded-lg text-sm font-semibold text-gray-700 mb-4 border">
            <div className="md:col-span-5">Category</div>
            <div className="md:col-span-5">Sub-Category</div>
            <div className="md:col-span-2">Action</div>
          </div>
          <p className="text-xs text-gray-500 mb-4 -mt-2 px-1">Issue Title and DTC Code appear as full-width fields directly under Category for each issue below.</p>

          {/* Vehicle Issues List - Advanced */}
          <div className="space-y-4">
            {editIssueFields.map((issue, index) => (
              <div 
                key={`issue-${issue.id}`} 
                className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 border rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow"
              >
                
                {/* Category Column */}
                <div className="md:col-span-5">
                  <div className="space-y-3">
                    <div className="h-6 flex items-center">
                      <Label className="text-xs font-medium text-gray-600">Category</Label>
                    </div>
                    <select
                      value={issue.category || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        const idx = editIssueFields.findIndex(f => f.id === issue.id);
                        if (idx >= 0) {
                          updateEditIssueAt(idx, {
                            ...editIssueFields[idx],
                            category: value,
                            title: issue.manualTitle ? editIssueFields[idx].title : ''
                          });
                        }
                      }}
                      className="h-10 w-full rounded-md border-2 border-gray-200 bg-white px-3 text-sm text-gray-700 focus:border-orange-300 focus:outline-none"
                      data-testid={`edit-jobcard-issue-category-${issue.id}`}
                    >
                      <option value="">
                        {getDbCategories().length === 0 
                          ? 'No categories in DB' 
                          : `Category (${getDbCategories().length})`}
                      </option>
                      {getDbCategories().map(cat => (
                        <option key={cat} value={cat}>{capitalizeWords(cat)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Sub-Category Column */}
                <div className="md:col-span-5">
                  <div className="space-y-3">
                    <div className="h-6 flex items-center">
                      <Label className="text-xs font-medium text-gray-600">Sub-Category</Label>
                    </div>
                    <select
                      value={issue.subcategory || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        const idx = editIssueFields.findIndex(f => f.id === issue.id);
                        if (idx >= 0) {
                          updateEditIssueAt(idx, { ...editIssueFields[idx], subcategory: value });
                        }
                      }}
                      disabled={!issue.category}
                      className={`h-10 w-full rounded-md border-2 px-3 text-sm focus:outline-none ${
                        !issue.category
                          ? 'border-gray-200 bg-gray-100 cursor-not-allowed text-gray-400'
                          : 'border-gray-200 bg-white text-gray-700 focus:border-orange-300'
                      }`}
                      data-testid={`edit-jobcard-issue-subcategory-${issue.id}`}
                    >
                      <option value="">
                        {issue.category
                          ? (getDbSubcategories(issue.category).length === 0
                              ? `No subcategories`
                              : `Sub-Category (${getDbSubcategories(issue.category).length})`)
                          : 'Select category first'}
                      </option>
                      {getDbSubcategories(issue.category).map(sub => (
                        <option key={sub} value={sub}>{capitalizeWords(sub)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Remove Button */}
                <div className="md:col-span-2 flex justify-center">
                  {editIssueFields.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeVehicleIssue(issue.id)}
                      className="text-red-600 border-red-300 hover:bg-red-100"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {/* Issue Title - full-width row directly under Category/Sub-Category, sized to fit long titles */}
                <div className="md:col-span-12">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between h-6">
                      <Label className="text-xs font-medium text-gray-600">
                        {issue.manualTitle ? '✏️ Manual' : '📋 Select Title'}
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => updateVehicleIssue(issue.id, 'manualTitle', !issue.manualTitle)}
                        className="text-xs h-6 px-2 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-full"
                      >
                        {issue.manualTitle ? '📋' : '✏️'}
                      </Button>
                    </div>
                    
                    {issue.manualTitle ? (
                      <Input
                        key={`title-input-${issue.id}`}
                        placeholder="Enter custom issue title - describe the problem in detail"
                        value={issue.title}
                        onChange={(e) => updateVehicleIssue(issue.id, 'title', e.target.value)}
                        className="w-full h-10 border-2 border-gray-200 focus:border-orange-300"
                      />
                    ) : (() => {
                        const opts = getFilteredIssueTitles(issue.category, issue.subcategory);
                        return (
                          <>
                            <select
                              value={issue.title || ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                const idx = editIssueFields.findIndex(f => f.id === issue.id);
                                if (idx >= 0) {
                                  updateEditIssueAt(idx, { ...editIssueFields[idx], title: value });
                                }
                              }}
                              disabled={!issue.category}
                              className={`h-10 w-full rounded-md border-2 px-3 text-sm focus:outline-none ${
                                !issue.category
                                  ? 'border-gray-200 bg-gray-100 cursor-not-allowed text-gray-400'
                                  : 'border-gray-200 bg-white text-gray-700 focus:border-orange-300'
                              }`}
                              data-testid={`edit-jobcard-issue-title-${issue.id}`}
                            >
                              <option value="">
                                {!issue.category
                                  ? 'Select category first'
                                  : opts.length === 0
                                    ? `No problems in DB for ${capitalizeWords(issue.category)}${issue.subcategory ? ' → ' + capitalizeWords(issue.subcategory) : ''}`
                                    : `Select ${capitalizeWords(issue.category)} issue (${opts.length} available)`}
                              </option>
                              {opts.map((issueOption) => (
                                <option key={issueOption.value} value={issueOption.value}>{issueOption.label}</option>
                              ))}
                            </select>
                            {issue.category && opts.length === 0 && (
                              <div className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                                💡 No matching problems in Enhanced Vehicle Problems Database. Use <strong>Manual</strong> mode.
                              </div>
                            )}
                          </>
                        );
                      })()}
                  </div>
                </div>

                {/* DTC Code - full-width row directly under Issue Title, sized to fit the code + description */}
                <div className="md:col-span-12">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between h-6">
                      <Label className="text-xs font-medium text-gray-600">
                        {issue.manualDTC ? '✏️ DTC Code (Manual)' : '📋 DTC Code'}
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => updateVehicleIssue(issue.id, 'manualDTC', !issue.manualDTC)}
                        className="text-xs h-6 px-2 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-full"
                      >
                        {issue.manualDTC ? '📋' : '✏️'}
                      </Button>
                    </div>

                    {issue.manualDTC ? (
                      <Input
                        key={`dtc-input-${issue.id}`}
                        placeholder="DTC Code"
                        value={issue.dtc}
                        onChange={(e) => updateVehicleIssue(issue.id, 'dtc', e.target.value.toUpperCase())}
                        className="w-full h-10 border-2 border-gray-200 focus:border-orange-300"
                        maxLength="10"
                        data-testid={`edit-jobcard-issue-dtc-input-${issue.id}`}
                      />
                    ) : (
                      <Select
                        key={`dtc-select-${issue.id}`}
                        value={issue.dtc || ""}
                        onValueChange={(value) => updateVehicleIssue(issue.id, 'dtc', value)}
                      >
                        <SelectTrigger className="h-10 w-full border-2 border-gray-200 focus:border-orange-300" data-testid={`edit-jobcard-issue-dtc-select-${issue.id}`}>
                          <SelectValue placeholder="DTC Code" className="truncate" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60 z-50 w-[--radix-select-trigger-width]">
                          {/* Engine DTC Codes */}
                          <SelectItem value="P0300">P0300 - Random/Multiple Cylinder Misfire</SelectItem>
                          <SelectItem value="P0301">P0301 - Cylinder 1 Misfire Detected</SelectItem>
                          <SelectItem value="P0302">P0302 - Cylinder 2 Misfire Detected</SelectItem>
                          <SelectItem value="P0303">P0303 - Cylinder 3 Misfire Detected</SelectItem>
                          <SelectItem value="P0304">P0304 - Cylinder 4 Misfire Detected</SelectItem>
                          <SelectItem value="P0171">P0171 - System Too Lean (Bank 1)</SelectItem>
                          <SelectItem value="P0174">P0174 - System Too Lean (Bank 2)</SelectItem>
                          <SelectItem value="P0420">P0420 - Catalyst System Efficiency Below Threshold</SelectItem>
                          <SelectItem value="P0430">P0430 - Catalyst System Efficiency Below Threshold (Bank 2)</SelectItem>
                          {/* Transmission DTC Codes */}
                          <SelectItem value="P0700">P0700 - Transmission Control System</SelectItem>
                          <SelectItem value="P0715">P0715 - Input/Turbine Speed Sensor Circuit</SelectItem>
                          <SelectItem value="P0720">P0720 - Output Speed Sensor Circuit</SelectItem>
                          {/* Brake/ABS DTC Codes */}
                          <SelectItem value="C0200">C0200 - ABS System Malfunction</SelectItem>
                          <SelectItem value="C0201">C0201 - ABS Speed Sensor</SelectItem>
                          {/* Electrical DTC Codes */}
                          <SelectItem value="B0001">B0001 - Battery Voltage Low</SelectItem>
                          <SelectItem value="U0100">U0100 - Lost Communication with ECM/PCM</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>

                {/* Per-issue photo/video attachments */}
                <div className="md:col-span-12 border-t pt-3 mt-1">
                  <p className="text-xs font-medium text-gray-600 mb-1">Attachments for this issue</p>
                  <IssueMediaAttachments jobCardId={jobCard.id} issueId={issue.id} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label htmlFor="notes">Additional Notes</Label>
          <Textarea
            id="notes"
            {...register('notes')}
            rows="3"
            placeholder="Any additional notes or comments"
          />
        </div>

        <JobCardUsedItems
          items={usedItems}
          onItemsChange={setUsedItems}
          vehicleMake={jobCard.vehicle_make}
          vehicleModel={jobCard.vehicle_model}
        />

        {/* Scanning Report Section */}
        <div className="border rounded-lg p-4 bg-red-50">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Diagnostic Scanning Report</h3>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label>Upload Scanning Report PDF</Label>
              <div className="flex space-x-2">
                <Input
                  {...register("scanning_report_filename")}
                  placeholder="diagnostic_report.pdf"
                  className="flex-1"
                  readOnly
                />
                <input
                  type="file"
                  accept=".pdf"
                  multiple
                  style={{ display: 'none' }}
                  ref={(el) => window.editPdfFileInput = el}
                  onChange={async (e) => {
                    const selected = Array.from(e.target.files || []);
                    if (selected.length > 0) {
                      try {
                        const formData = new FormData();
                        selected.forEach(f => formData.append('files', f));
                        const response = await axios.post(`${API}/jobcards/${jobCard.id}/upload-report`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        setValue('scanning_report_filename', selected.map(f => f.name).join(', '));
                        toast.success(`${selected.length} report(s) uploaded`);
                      } catch (error) {
                        toast.error('Failed to upload PDF: ' + (error.response?.data?.detail || error.message));
                      }
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
                  onClick={() => window.editPdfFileInput?.click()}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add PDF(s)
                </Button>
              </div>
              {watch("scanning_report_filename") && (
                <p className="text-sm text-green-600 mt-1">Current: {watch("scanning_report_filename")}</p>
              )}
            </div>
          </div>
        </div>

        {/* Vehicle Photos Section */}
        <div className="border rounded-lg p-4 bg-cyan-50">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Vehicle Photos</h3>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label>Upload Vehicle Photos</Label>
              <div className="flex space-x-2">
                <Input
                  {...register("photo_filenames")}
                  placeholder="photo1.jpg, photo2.jpg"
                  className="flex-1"
                  readOnly
                />
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  ref={(el) => window.editPhotoFileInput = el}
                  onChange={async (e) => {
                    const files = Array.from(e.target.files);
                    if (files.length > 0) {
                      try {
                        const formData = new FormData();
                        files.forEach(f => formData.append('files', f));
                        const response = await axios.post(`${API}/jobcards/${jobCard.id}/upload-photos`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        const fileNames = files.map(f => f.name).join(', ');
                        const existing = watch("photo_filenames");
                        setValue('photo_filenames', existing ? `${existing}, ${fileNames}` : fileNames);
                        toast.success(`${files.length} photo(s) uploaded`);
                      } catch (error) {
                        toast.error('Failed to upload photos: ' + (error.response?.data?.detail || error.message));
                      }
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                  onClick={() => window.editPhotoFileInput?.click()}
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Add Photos
                </Button>
              </div>
              {watch("photo_filenames") && (
                <p className="text-sm text-green-600 mt-1">Files: {watch("photo_filenames")}</p>
              )}
            </div>
          </div>
        </div>

        {/* Vehicle Videos Section */}
        <div className="border rounded-lg p-4 bg-purple-50">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Vehicle Videos (Max 10)</h3>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label>Upload Vehicle Videos</Label>
              <div className="flex space-x-2">
                <Input
                  {...register("video_filenames")}
                  placeholder="video1.mp4, video2.mov"
                  className="flex-1"
                  readOnly
                />
                <input
                  type="file"
                  accept="video/mp4,video/mov,video/avi,video/wmv,video/mkv,video/webm"
                  multiple
                  style={{ display: 'none' }}
                  ref={(el) => window.editVideoFileInput = el}
                  onChange={async (e) => {
                    const files = Array.from(e.target.files);
                    if (files.length > 10) {
                      toast.error('Maximum 10 videos allowed.');
                      e.target.value = '';
                      return;
                    }
                    const oversizedFiles = files.filter(f => f.size > 100 * 1024 * 1024);
                    if (oversizedFiles.length > 0) {
                      toast.error(`Some videos are too large (max 100MB): ${oversizedFiles.map(f => f.name).join(', ')}`);
                      return;
                    }
                    if (files.length > 0) {
                      try {
                        const formData = new FormData();
                        files.forEach(f => formData.append('files', f));
                        toast.info(`Uploading ${files.length} video(s)...`);
                        const response = await axios.post(`${API}/jobcards/${jobCard.id}/upload-videos`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        setValue('video_filenames', response.data.video_filenames || '');
                        toast.success(`${files.length} video(s) uploaded successfully`);
                      } catch (error) {
                        toast.error('Failed to upload videos: ' + (error.response?.data?.detail || error.message));
                      }
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-300"
                  onClick={() => window.editVideoFileInput?.click()}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Videos
                </Button>
              </div>
              <div className="mt-2 text-sm text-gray-600">
                <p>Supported: MP4, MOV, AVI, WMV, MKV, WebM (max 100MB each)</p>
              </div>
              {watch("video_filenames") && (
                <div className="mt-3 p-3 bg-purple-100 rounded border">
                  <p className="text-sm font-medium text-purple-800 mb-2">Uploaded Videos:</p>
                  <div className="space-y-1">
                    {watch("video_filenames").split(', ').filter(name => name.trim()).map((filename, idx) => (
                      <div key={idx} className="flex items-center text-sm text-purple-700">
                        <span className="mr-2">🎥</span>
                        <span className="truncate">{filename}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </form>

      {/* Parts Request — sends requested parts to a draft Purchase Order */}
      <PartsRequestManager jobCard={jobCard} />

      {/* Enhanced Work Hold and Time Tracking Components */}
      <WorkHoldManager 
        jobCard={jobCard} 
        onUpdate={(updatedJobCard) => {
          // Refresh data without closing the edit modal
          (onJobCardRefresh || onJobCardUpdated)();
        }}
      />
      
      <TimeTracker 
        jobCard={jobCard} 
        onUpdate={(updatedJobCard) => {
          // Refresh data without closing the edit modal
          (onJobCardRefresh || onJobCardUpdated)();
        }}
      />

      <div className="pt-4">
        <Button 
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
          className="w-full bg-blue-600 hover:bg-blue-700"
        >
          {isSubmitting ? "Updating..." : "Update Job Card"}
        </Button>
      </div>
    </div>
  );
};

// Multi-Item Support Component

const JobCardsTestSection = ({ onUpdate }) => {
  const [jobCards, setJobCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forceUpdate, setForceUpdate] = useState(0); // Force re-render
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterTechnician, setFilterTechnician] = useState('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedJobCard, setSelectedJobCard] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  const searchInputRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Fetch data
  const fetchJobCards = async () => {
    try {
      const response = await axios.get(`${API}/jobcards`);
      setJobCards(response.data || []);
    } catch (error) {
      console.error("Error fetching job cards:", error);
      setJobCards([]);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data || []);
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles`);
      setVehicles(response.data || []);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      setVehicles([]);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data || []);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setEmployees([]);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchJobCards(),
        fetchCustomers(),
        fetchVehicles(),
        fetchEmployees()
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  // --- Running-timer tracking (active work sessions) ---
  const [now, setNow] = useState(Date.now());
  const [activeSessions, setActiveSessions] = useState({ active_count: 0, stale_count: 0, sessions: [], stale_threshold_minutes: 240 });

  const fetchActiveSessions = async () => {
    try {
      const res = await axios.get(`${API}/jobcards/active-sessions`);
      setActiveSessions(res.data || { active_count: 0, stale_count: 0, sessions: [] });
    } catch (e) {
      // silent — non-critical
    }
  };

  useEffect(() => {
    fetchActiveSessions();
    const tick = setInterval(() => setNow(Date.now()), 30000);
    const poll = setInterval(fetchActiveSessions, 60000);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, []);

  const STALE_MINUTES = activeSessions.stale_threshold_minutes || 240;

  const getElapsedMinutes = (startedAt) => {
    if (!startedAt) return 0;
    const s = new Date(startedAt).getTime();
    if (isNaN(s)) return 0;
    return Math.max(0, (now - s) / 60000);
  };

  const fmtElapsed = (mins) => {
    const m = Math.max(0, Math.round(mins));
    const h = Math.floor(m / 60);
    return h > 0 ? `${h}h ${m % 60}m` : `${m}m`;
  };

  const stopRunningTimer = async (jobCardId, sessionId) => {
    try {
      await axios.put(`${API}/jobcards/${jobCardId}/stop-work`, { session_id: sessionId, break_time_minutes: 0 });
      toast.success('Timer stopped and logged');
      await Promise.all([fetchJobCards(), fetchActiveSessions()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to stop timer');
    }
  };

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Simple working filter (matching User Management pattern)
  const filteredJobCards = jobCards.filter(card => {
    // Search filter
    const q = (searchTerm || '').trim().toLowerCase();
    const matchesSearch = !q ||
      (card.job_number && card.job_number.toLowerCase().includes(q)) ||
      (card.customer_name && card.customer_name.toLowerCase().includes(q)) ||
      (card.customer_contact && card.customer_contact.toLowerCase().includes(q)) ||
      (card.vehicle_make && card.vehicle_make.toLowerCase().includes(q)) ||
      (card.vehicle_model && card.vehicle_model.toLowerCase().includes(q)) ||
      (card.vehicle_series && card.vehicle_series.toLowerCase().includes(q)) ||
      // VIN (full or partial — most users search by last 6 digits)
      (card.vin && card.vin.toLowerCase().includes(q)) ||
      // License plate / registration number
      (card.vehicle_reg && card.vehicle_reg.toLowerCase().includes(q)) ||
      (card.license_plate && card.license_plate.toLowerCase().includes(q)) ||
      (card.description && card.description.toLowerCase().includes(q)) ||
      (card.title && card.title.toLowerCase().includes(q));

    // Status filter
    const matchesStatus = filterStatus === 'all' || card.status === filterStatus;

    // Priority filter  
    const matchesPriority = filterPriority === 'all' || card.priority === filterPriority;

    // Technician filter
    const matchesTechnician = filterTechnician === 'all' || 
      (filterTechnician === 'unassigned' && (!card.assigned_technician || card.assigned_technician === 'unassigned' || card.assigned_technician === '')) ||
      card.assigned_technician === filterTechnician;

    return matchesSearch && matchesStatus && matchesPriority && matchesTechnician;
  }).sort((a, b) => {
    // Newest first
    const aKey = a.created_at || a.created_date || a.job_number || '';
    const bKey = b.created_at || b.created_date || b.job_number || '';
    return String(bKey).localeCompare(String(aKey));
  });

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  // Statistics
  const stats = {
    total: jobCards.length,
    created: jobCards.filter(jc => jc.status === 'created').length,
    assigned: jobCards.filter(jc => jc.status === 'assigned').length,
    inProgress: jobCards.filter(jc => jc.status === 'in-progress').length,
    onHold: jobCards.filter(jc => jc.status === 'on-hold').length,
    waitingParts: jobCards.filter(jc => jc.status === 'waiting-parts').length,
    qualityCheck: jobCards.filter(jc => jc.status === 'quality-check').length,
    completed: jobCards.filter(jc => jc.status === 'completed').length,
    delivered: jobCards.filter(jc => jc.status === 'delivered').length,
    invoiced: jobCards.filter(jc => jc.status === 'invoiced').length,
    unassigned: jobCards.filter(jc => !jc.assigned_technician || jc.assigned_technician === 'unassigned' || jc.assigned_technician === '').length,
    totalRevenue: jobCards.reduce((sum, jc) => sum + (jc.total_cost || 0), 0)
  };

  const handleCreateJobCard = () => {
    setSelectedJobCard(null);
    setIsCreateModalOpen(true);
  };

  const handleEditJobCard = (jobCard) => {
    setSelectedJobCard(jobCard);
    setIsEditModalOpen(true);
  };

  const handleViewJobCard = (jobCard) => {
    setSelectedJobCard(jobCard);
    setIsViewModalOpen(true);
  };

  const handleJobCardCreated = async () => {
    await fetchJobCards();
    setIsCreateModalOpen(false);
    if (onUpdate) onUpdate();
    toast.success('Job card created successfully!');
  };

  const handleJobCardUpdated = async () => {
    console.log('🔄 Refreshing job card after update...');
    await fetchJobCards();
    
    // Refresh the selectedJobCard with updated data from the API
    if (selectedJobCard) {
      try {
        console.log(`📡 Fetching updated data for job card: ${selectedJobCard.id}`);
        const response = await axios.get(`${API}/jobcards/${selectedJobCard.id}`);
        console.log('✅ Updated job card data:', response.data);
        setSelectedJobCard(response.data);
        console.log('🔄 selectedJobCard state updated');
      } catch (error) {
        console.error('❌ Error refreshing job card details:', error);
      }
    }
    
    setIsEditModalOpen(false);
    if (onUpdate) onUpdate();
    toast.success('Job card updated successfully!');
  };

  // Silent refresh used by Time Tracking / Work Hold actions — refreshes data
  // WITHOUT closing the edit modal or showing the "updated" toast.
  const handleJobCardRefresh = async () => {
    if (selectedJobCard) {
      try {
        const response = await axios.get(`${API}/jobcards/${selectedJobCard.id}`);
        setSelectedJobCard(response.data);
      } catch (error) {
        console.error('Error refreshing job card details:', error);
      }
    }
    // Refresh the list, the per-card timer badges and the Running Timers panel
    // so starting/stopping a timer reflects instantly without a manual page refresh.
    await Promise.all([fetchJobCards(), fetchActiveSessions()]);
    if (onUpdate) onUpdate();
  };

  const handleDeleteJobCard = async (jobCardId) => {
    if (window.confirm('Are you sure you want to delete this job card?')) {
      try {
        await axios.delete(`${API}/jobcards/${jobCardId}`);
        await fetchJobCards();
        if (onUpdate) onUpdate();
        toast.success('Job card deleted successfully!');
      } catch (error) {
        console.error('Error deleting job card:', error);
        toast.error('Failed to delete job card');
      }
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      'created': { color: 'bg-gray-100 text-gray-800', icon: FileText, label: 'Created' },
      'assigned': { color: 'bg-blue-100 text-blue-800', icon: Users, label: 'Assigned' },
      'in-progress': { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'In Progress' },
      'on-hold': { color: 'bg-red-100 text-red-800', icon: AlertTriangle, label: 'On Hold' },
      'waiting-parts': { color: 'bg-purple-100 text-purple-800', icon: Package, label: 'Waiting Parts' },
      'quality-check': { color: 'bg-indigo-100 text-indigo-800', icon: CheckCircle, label: 'Quality Check' },
      'completed': { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
      'delivered': { color: 'bg-emerald-100 text-emerald-800', icon: Truck, label: 'Delivered' },
      'invoiced': { color: 'bg-teal-100 text-teal-800', icon: FileCheck, label: 'Invoiced' },
      'rework': { color: 'bg-rose-100 text-rose-800', icon: RotateCcw, label: 'Rework' }
    };
    return configs[status] || configs['created'];
  };

  const getPriorityConfig = (priority) => {
    const configs = {
      'low': { color: 'bg-green-100 text-green-800', label: '🔵 Low' },
      'medium': { color: 'bg-yellow-100 text-yellow-800', label: '🟡 Medium' },
      'high': { color: 'bg-orange-100 text-orange-800', label: '🟠 High' },
      'urgent': { color: 'bg-red-100 text-red-800', label: '🔴 Urgent' }
    };
    return configs[priority] || configs['medium'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading job cards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            📋 Job Cards Management
          </h2>
          <p className="text-gray-600">
            Enhanced job card system for comprehensive work order management
          </p>
        </div>
        <Button 
          onClick={handleCreateJobCard}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Job Card
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-2">
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold">{stats.total}</div>
                <div className="text-xs opacity-90">Total</div>
              </div>
              <FileText className="h-5 w-5 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-gray-500 to-gray-600 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold">{stats.created}</div>
                <div className="text-xs opacity-90">Created</div>
              </div>
              <FileText className="h-5 w-5 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-blue-400 to-blue-500 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold">{stats.assigned}</div>
                <div className="text-xs opacity-90">Assigned</div>
              </div>
              <Users className="h-5 w-5 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold">{stats.unassigned}</div>
                <div className="text-xs opacity-90">Unassigned</div>
              </div>
              <AlertTriangle className="h-5 w-5 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold">{stats.inProgress}</div>
                <div className="text-xs opacity-90">In Progress</div>
              </div>
              <Clock className="h-5 w-5 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-red-500 to-red-600 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold">{stats.onHold}</div>
                <div className="text-xs opacity-90">On Hold</div>
              </div>
              <AlertTriangle className="h-5 w-5 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold">{stats.waitingParts}</div>
                <div className="text-xs opacity-90">Waiting Parts</div>
              </div>
              <Package className="h-5 w-5 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold">{stats.qualityCheck}</div>
                <div className="text-xs opacity-90">Quality Check</div>
              </div>
              <CheckCircle className="h-5 w-5 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold">{stats.completed}</div>
                <div className="text-xs opacity-90">Completed</div>
              </div>
              <CheckCircle className="h-5 w-5 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold">{stats.delivered}</div>
                <div className="text-xs opacity-90">Delivered</div>
              </div>
              <Truck className="h-5 w-5 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Statistics Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-r from-teal-500 to-teal-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{stats.invoiced}</div>
                <div className="text-sm opacity-90">Invoiced</div>
              </div>
              <FileCheck className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">₹{stats.totalRevenue.toLocaleString()}</div>
                <div className="text-sm opacity-90">Total Revenue</div>
              </div>
              <DollarSign className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-pink-500 to-pink-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{Math.round((stats.completed / stats.total) * 100) || 0}%</div>
                <div className="text-sm opacity-90">Completion Rate</div>
              </div>
              <Activity className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-lg">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by job #, customer, vehicle make/model, VIN, license plate..."
              value={searchTerm}
              onChange={handleSearch}
              className="pl-10 no-uppercase"
              data-testid="jobcards-search-input"
            />
          </div>
        </div>

        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="created">Created</SelectItem>
            <SelectItem value="assigned">Assigned</SelectItem>
            <SelectItem value="in-progress">In Progress</SelectItem>
            <SelectItem value="on-hold">On Hold</SelectItem>
            <SelectItem value="waiting-parts">Waiting Parts</SelectItem>
            <SelectItem value="quality-check">Quality Check</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="invoiced">Invoiced</SelectItem>
            <SelectItem value="rework">Rework</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterTechnician} onValueChange={setFilterTechnician}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Technician Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Technicians</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {employees
              .filter(employee => 
                employee.status === 'active' && 
                (employee.department === 'service' || 
                 employee.department === 'technical' || 
                 employee.role === 'technician' || 
                 employee.position?.toLowerCase().includes('technician') ||
                 employee.position?.toLowerCase().includes('mechanic') ||
                 employee.position?.toLowerCase().includes('advisor'))
              )
              .map(employee => (
                <SelectItem key={employee.id} value={employee.name}>
                  {employee.name} - {employee.position || employee.role}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>

        <Button 
          variant="outline" 
          onClick={() => {
            setSearchTerm('');
            setFilterStatus('all');
            setFilterPriority('all');
            setFilterTechnician('all');
          }}
        >
          Clear Filters
        </Button>
      </div>

      {/* Running Timers panel — surfaces running/forgotten work timers */}
      {activeSessions.active_count > 0 && (
        <div className={`mb-6 rounded-lg border p-4 ${activeSessions.stale_count > 0 ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`} data-testid="running-timers-panel">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className={`inline-block h-3 w-3 rounded-full animate-pulse ${activeSessions.stale_count > 0 ? 'bg-red-500' : 'bg-green-500'}`}></span>
            <span className="font-semibold text-gray-800">
              {activeSessions.active_count} timer{activeSessions.active_count > 1 ? 's' : ''} running
            </span>
            {activeSessions.stale_count > 0 && (
              <Badge className="bg-red-600 text-white" data-testid="stale-timers-badge">
                ⚠️ {activeSessions.stale_count} running over {Math.round(STALE_MINUTES / 60)}h — possibly forgotten
              </Badge>
            )}
          </div>
          <div className="space-y-2">
            {activeSessions.sessions.map((s) => {
              const mins = getElapsedMinutes(s.started_at);
              const stale = mins >= STALE_MINUTES;
              return (
                <div key={s.session_id || s.job_card_id} className={`flex items-center justify-between rounded-md px-3 py-2 text-sm bg-white border ${stale ? 'border-red-300' : 'border-gray-200'}`} data-testid={`running-timer-row-${s.session_id || s.job_card_id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`inline-block h-2.5 w-2.5 rounded-full animate-pulse ${stale ? 'bg-red-500' : 'bg-green-500'}`}></span>
                    <span className="font-medium">{s.job_number}</span>
                    <span className="text-gray-500 truncate">· {s.technician_name || 'Unknown'} {(s.vehicle || s.vehicle_reg) ? `· ${[s.vehicle, s.vehicle_reg].filter(Boolean).join(' · ')}` : ''}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`font-semibold ${stale ? 'text-red-600' : 'text-green-700'}`}>{fmtElapsed(mins)}</span>
                    <Button size="sm" variant="outline" className="h-7 text-red-600 border-red-300 hover:bg-red-50" onClick={() => stopRunningTimer(s.job_card_id, s.session_id)} data-testid={`stop-timer-btn-${s.session_id || s.job_card_id}`}>
                      Stop
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Job Cards Grid */}
      {filteredJobCards.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Job Cards Found</h3>
          <p className="text-gray-600 mb-4">
            {filterStatus !== 'all' || filterPriority !== 'all' 
              ? "No job cards match your current filters." 
              : "Get started by creating your first job card."
            }
          </p>
          {filterStatus === 'all' && filterPriority === 'all' && (
            <Button onClick={handleCreateJobCard} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Create First Job Card
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredJobCards.map((jobCard) => {
            const statusConfig = getStatusConfig(jobCard.status);
            const priorityConfig = getPriorityConfig(jobCard.priority);
            const StatusIcon = statusConfig.icon;
            
            return (
              <Card key={jobCard.id} className="hover:shadow-lg transition-shadow duration-200">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg font-semibold mb-1">
                        {jobCard.job_number || jobCard.id}
                      </CardTitle>
                      <p className="text-sm text-gray-600">{jobCard.title}</p>
                    </div>
                    <div className="flex flex-col items-end space-y-1">
                      {(() => {
                        const activeSessions = jobCard.active_sessions || (jobCard.current_session ? [jobCard.current_session] : []);
                        if (activeSessions.length === 0) return null;
                        const earliest = activeSessions.reduce((min, s) => {
                          const t = new Date(s.started_at).getTime();
                          return (!min || t < min) ? t : min;
                        }, null);
                        const mins = getElapsedMinutes(earliest);
                        const stale = mins >= STALE_MINUTES;
                        return (
                          <Badge className={`${stale ? 'bg-red-600' : 'bg-green-600'} text-white text-xs flex items-center gap-1`} data-testid={`jobcard-timer-badge-${jobCard.id}`}>
                            <span className="inline-block h-2 w-2 rounded-full bg-white animate-pulse"></span>
                            {stale ? '⚠️ ' : ''}{fmtElapsed(mins)}{activeSessions.length > 1 ? ` · ${activeSessions.length} techs` : ''}
                          </Badge>
                        );
                      })()}
                      <Badge className={`${statusConfig.color} text-xs`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusConfig.label}
                      </Badge>
                      <Badge className={`${priorityConfig.color} text-xs`}>
                        {priorityConfig.label}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="pb-4">
                  <div className="space-y-3">
                    {/* Vehicle Info */}
                    {(jobCard.vehicle_make || jobCard.vehicle_model || jobCard.vehicle_reg || jobCard.license_plate || jobCard.vin) && (
                      <div className="flex items-center text-sm text-gray-600" data-testid={`jobcard-vehicle-info-${jobCard.id}`}>
                        <Car className="w-4 h-4 mr-2" />
                        <span>
                          {jobCard.vehicle_year} {jobCard.vehicle_make} {jobCard.vehicle_model}
                          {(jobCard.vehicle_reg || jobCard.license_plate) && ` (${jobCard.vehicle_reg || jobCard.license_plate})`}
                        </span>
                      </div>
                    )}
                    {jobCard.vin && (
                      <div className="flex items-center text-sm text-gray-600" data-testid={`jobcard-vin-info-${jobCard.id}`}>
                        <span className="w-4 mr-2 text-xs font-bold text-gray-400 shrink-0">VIN</span>
                        <span className="font-mono text-xs truncate">{jobCard.vin}</span>
                      </div>
                    )}
                    
                    {/* Customer Info */}
                    {jobCard.customer_name && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Users className="w-4 h-4 mr-2" />
                        <span>{jobCard.customer_name}</span>
                      </div>
                    )}
                    
                    {/* Technician */}
                    {jobCard.assigned_technician && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Wrench className="w-4 h-4 mr-2" />
                        <span>{jobCard.assigned_technician}</span>
                      </div>
                    )}
                    
                    {/* Description */}
                    {jobCard.description && jobCard.description.length > 0 && (
                      <div className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
                        {jobCard.description.length > 100 
                          ? `${jobCard.description.substring(0, 100)}...` 
                          : jobCard.description
                        }
                      </div>
                    )}
                    
                    {/* Cost */}
                    {jobCard.total_cost && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Total Cost:</span>
                        <span className="font-semibold text-green-600">
                          ₹{jobCard.total_cost.toLocaleString()}
                        </span>
                      </div>
                    )}
                    
                    {/* Dates */}
                    <div className="text-xs text-gray-500">
                      Created: {new Date(jobCard.created_at || new Date()).toLocaleDateString()}
                      {jobCard.estimated_completion && (
                        <div>
                          Due: {new Date(jobCard.estimated_completion).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
                
                <div className="px-6 pb-4">
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleViewJobCard(jobCard)}
                      className="flex-1"
                      data-testid={`jobcard-view-btn-${jobCard.id}`}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleEditJobCard(jobCard)}
                      className="flex-1"
                      data-testid={`jobcard-edit-btn-${jobCard.id}`}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleDeleteJobCard(jobCard.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Summary */}
      {filteredJobCards.length > 0 && (
        <div className="text-center text-sm text-gray-600">
          Showing {filteredJobCards.length} of {jobCards.length} job cards
          {stats.totalRevenue > 0 && (
            <span className="ml-4">
              Total Revenue: ₹{stats.totalRevenue.toLocaleString()}
            </span>
          )}
        </div>
      )}

      {/* Create Job Card Modal */}
      <Dialog 
        open={isCreateModalOpen} 
        onOpenChange={(open) => {
          // Allow opening, but show confirmation before closing
          if (open) {
            setIsCreateModalOpen(true);
          } else {
            // Show confirmation before closing
            const shouldClose = window.confirm('Are you sure you want to close? Any unsaved changes will be lost.');
            if (shouldClose) {
              setIsCreateModalOpen(false);
            }
          }
        }}
      >
        <DialogContent 
          className="max-w-4xl max-h-[90vh] overflow-y-auto"
          onPointerDownOutside={(e) => {
            // Show confirmation before closing on outside click
            const shouldClose = window.confirm('Are you sure you want to close? Any unsaved changes will be lost.');
            if (!shouldClose) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Create New Job Card</DialogTitle>
          </DialogHeader>
          <AddJobCardForm 
            customers={customers}
            vehicles={vehicles}
            employees={employees}
            onJobCardAdded={handleJobCardCreated}
            onCancel={() => setIsCreateModalOpen(false)} // Explicit close function
          />
        </DialogContent>
      </Dialog>

      {/* Edit Job Card Modal */}
      <Dialog 
        open={isEditModalOpen} 
        onOpenChange={(open) => {
          // Allow opening, but show confirmation before closing
          if (open) {
            setIsEditModalOpen(true);
          } else {
            // Show confirmation before closing
            const shouldClose = window.confirm('Are you sure you want to close? Any unsaved changes will be lost.');
            if (shouldClose) {
              setIsEditModalOpen(false);
            }
          }
        }}
      >
        <DialogContent 
          className="max-w-4xl max-h-[90vh] overflow-y-auto"
          onPointerDownOutside={(e) => {
            // Show confirmation before closing on outside click
            const shouldClose = window.confirm('Are you sure you want to close? Any unsaved changes will be lost.');
            if (!shouldClose) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Edit Job Card</DialogTitle>
          </DialogHeader>
          {selectedJobCard && (
            <EditJobCardForm 
              jobCard={selectedJobCard}
              customers={customers}
              vehicles={vehicles}
              employees={employees}
              onJobCardUpdated={handleJobCardUpdated}
              onJobCardRefresh={handleJobCardRefresh}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* View Job Card Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Job Card Details</DialogTitle>
          </DialogHeader>
          {selectedJobCard && (
            <JobCardDetailsView 
              jobCard={selectedJobCard}
              customers={customers}
              vehicles={vehicles}
              onClose={() => {
                setIsViewModalOpen(false);
                setSelectedJobCard(null);
              }}
              onEdit={() => {
                setIsViewModalOpen(false);
                setIsEditModalOpen(true);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ Invoice Payment Form Component ============

export { JobCardsTestSection };
